/**
 * Tutorial Sandbox System - Modern Spotlight Coachmarks
 *
 * Provides guided walkthrough overlays using a spotlight/cutout approach.
 * The tutorial panel positions itself near highlighted elements, with an
 * arrow pointing to the target. Users can interact with highlighted elements
 * through the spotlight cutout.
 *
 * Based on best practices from Shepherd.js, Driver.js, and modern onboarding UX.
 *
 * @module tutorial-sandbox
 * @license GPL-3.0-or-later
 */

import { createFocusTrap, disableKeepingFocus, setBusy } from './focus-trap.js';
import { getUIModeController } from './ui-mode-controller.js';
import { announceImmediate, announceError } from './announcer.js';
import { backGuardAnsweredPop } from './back-guard.js';
import {
  STORAGE_KEY_TUTORIAL_STATE,
  safeGetItem,
  safeSetItem,
} from './storage-keys.js';

/**
 * Tutorial step definition
 * @typedef {Object} TutorialStep
 * @property {string} title - Step title
 * @property {string} content - Step description (supports HTML)
 * @property {string} [highlightSelector] - CSS selector to highlight
 * @property {string} [position] - Preferred position: 'top', 'bottom', 'left', 'right', 'auto' (default)
 * @property {Object} [completion] - Optional completion criteria
 */

/**
 * Tutorial definition
 * @typedef {Object} Tutorial
 * @property {string} id - Unique tutorial identifier
 * @property {string} title - Tutorial title
 * @property {TutorialStep[]} steps - Array of tutorial steps
 */

// Tutorial state
let activeTutorial = null;
let currentStepIndex = 0;
let tutorialOverlay = null;
let triggerElement = null;
let previousFocus = null; // Store focus to restore on close
let preTutorialMode = null; // Store UI mode to restore on close
let preTutorialDensity = null; // Classic density to hand back after a forced switch
let tutorialForcedMode = false; // True while the tutorial itself switched the interface
let modeChangeUnsubscribe = null; // ui-mode subscription active while a tutorial runs
let densityChangeListener = null; // Classic density listener active while a tutorial runs
let completionListeners = [];
let stepCompleted = true;
let resizeObserver = null;
let isMinimized = false;
let drawerObserver = null;
let focusTrapCleanup = null; // Cleanup function for focus trap
let consecutiveFailures = 0; // Track consecutive step failures
let isNavigating = false; // Debounce navigation clicks
let isPaused = false; // Pause state for visibility changes
let targetRemovalObserver = null; // Watch for target removal
let surfaceObserver = null; // Watch for welcome -> project while a welcome-surface tour runs
let dialogObserver = null; // Watch for a user-opened dialog while a tutorial runs
let escapeOwnedElsewhere = false; // Did a dialog or the drawer own this Escape?
let dialogStandDown = null; // What the stand-down took away, so it can give it back
let currentTarget = null; // Currently highlighted target
let scrollYBeforeLock = 0; // Store scroll position for body lock
let didLockBodyScroll = false; // Avoid fighting other scroll locks (e.g. mobile drawer)
let wasAutoMinimized = false; // Track whether current minimized state was automatic
let isSettingUpStep = false; // Guard flag to prevent drawer observer interference during step setup

const MAX_CONSECUTIVE_FAILURES = 3;
// sessionStorage-scoped by design: progress should survive a reload within
// the same tab but not persist across browser sessions.
const TUTORIAL_STORAGE_KEY = 'tutorialProgress';
const TUTORIAL_PROGRESS_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

// Spotlight cutout padding around target element
const SPOTLIGHT_PADDING = 8;
const SPOTLIGHT_RADIUS = 8;
const PANEL_OFFSET = 16;

/**
 * Find the nearest scrollable parent container
 * @param {HTMLElement} element - Element to start search from
 * @returns {HTMLElement|null} - Nearest scrollable parent or null
 */
function findScrollableParent(element) {
  let parent = element.parentElement;

  while (parent && parent !== document.body) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    const overflowX = window.getComputedStyle(parent).overflowX;

    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowX === 'auto' ||
      overflowX === 'scroll'
    ) {
      // Check if it actually has scrollable content
      if (
        parent.scrollHeight > parent.clientHeight ||
        parent.scrollWidth > parent.clientWidth
      ) {
        return parent;
      }
    }

    parent = parent.parentElement;
  }

  return null;
}

/**
 * The highest point on screen where this element can actually be seen.
 *
 * The nearest scrollable ancestor is not the right answer: an ancestor whose
 * content happens to fit still clips with `overflow: auto`, and the scrollable
 * one may be further out and start at the top of the window. Measured on the
 * CI Firefox runner, where wider fonts make `#welcomeScreen`'s content fit so
 * it is not scrollable: aligning to the scrollable ancestor put the target at
 * y=48 under an 86px header, while `#welcomeScreen` itself was still clipping
 * at ~145. So take the lowest top edge of every clipping ancestor.
 *
 * Except the ones below the first box that scrolls: they move with the
 * element, so their edge is never in its way. A parameter group clips its
 * own content and starts 1px above its summary; counted, it put the summary
 * forever "too high", and each pass scrolled the Customizer sheet up and the
 * summary further out of view.
 *
 * @param {HTMLElement} el
 * @returns {number} Viewport y below which the element is not clipped
 */
function visibleTopBoundFor(el) {
  let bound = 0;
  let scrolls = false;
  let parent = el.parentElement;

  while (parent && parent !== document.documentElement) {
    const style = getComputedStyle(parent);
    scrolls ||= [style.overflowY, style.overflowX].some(
      (value) => value === 'auto' || value === 'scroll'
    );
    if (
      scrolls &&
      (style.overflowY !== 'visible' || style.overflowX !== 'visible')
    ) {
      bound = Math.max(bound, parent.getBoundingClientRect().top);
    }
    parent = parent.parentElement;
  }

  return bound;
}

/**
 * Bring a target into view inside its own scroll container, leaving room for
 * the halo.
 *
 * scrollIntoView with block:'center' centers against the window, not the
 * scroll container. On the welcome tour's Open-or-start step,
 * #welcomeScreen is the scroll container and its visible box starts 124px
 * down, so centering against the window parks the 476px panel at y=13, the
 * container clips its first 111px, and the halo's top edge and the panel's
 * heading disappear under the header.
 *
 * Centring inside the container's own client box instead keeps the whole
 * target, halo included, where it can be seen. A target too tall for the box
 * shows its top rather than its middle, since that is where a tour's subject
 * usually begins.
 *
 * @param {HTMLElement} el - The step's target
 * @param {{topInset?: number}} [options] - Pixels at the box's top that
 *   something sticky covers; the target is centered in the rest
 */
function scrollTargetIntoContainerView(el, { topInset = 0 } = {}) {
  const margin = SPOTLIGHT_PADDING + 8;
  const container = findScrollableParent(el);
  if (!container) {
    el.scrollIntoView({ behavior: 'auto', block: 'center' });
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const view = container.clientHeight - topInset;
  const elTopInContent =
    elRect.top - containerRect.top + container.scrollTop - topInset;

  const next =
    elRect.height + margin * 2 >= view
      ? elTopInContent - margin
      : elTopInContent - (view - elRect.height) / 2;

  container.scrollTop = Math.max(
    0,
    Math.min(next, container.scrollHeight - view)
  );
}

/**
 * Check if we're on mobile viewport
 * @returns {boolean}
 */
function isMobileViewport() {
  return window.innerWidth < 768;
}

/**
 * Check if element is visible using multiple DOM checks
 * More reliable than offsetParent alone for fixed/sticky positioned elements
 * @param {HTMLElement} element - Element to check
 * @returns {boolean}
 */
function isElementVisible(element) {
  if (!element) return false;

  // Quick style checks first
  const style = getComputedStyle(element);
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0'
  ) {
    return false;
  }

  // Check if element has dimensions
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) {
    return false;
  }

  // Use visualViewport for zoom-safe visibility checks
  const viewport = getEffectiveViewport();

  // Check if element is within viewport (at least partially)
  const inViewport =
    rect.top < viewport.height &&
    rect.bottom > 0 &&
    rect.left < viewport.width &&
    rect.right > 0;

  // For elements inside param panel, check if panel is visible (both mobile and desktop)
  if (isInsideParamPanel(element) && !isParamPanelOpen()) {
    return false;
  }

  return inViewport;
}

/**
 * Evaluate showWhen condition for dynamic step skipping
 * @param {Object|Function} showWhen - Condition object or function
 * @returns {boolean} - Whether step should be shown
 */
/** The steps the current layout shows, in order. */
function shownSteps() {
  return (activeTutorial?.steps || []).filter(
    (s) => !s.showWhen || evaluateShowWhenCondition(s.showWhen)
  );
}

function shownStepCount() {
  return shownSteps().length;
}

/** The 1-based place of a step among the shown steps. */
function shownStepNumber(stepIndex) {
  const steps = activeTutorial?.steps || [];
  let n = 0;
  for (let i = 0; i <= stepIndex && i < steps.length; i++) {
    if (!steps[i].showWhen || evaluateShowWhenCondition(steps[i].showWhen)) n++;
  }
  return Math.max(1, n);
}

function evaluateShowWhenCondition(showWhen) {
  // If showWhen is a function, call it
  if (typeof showWhen === 'function') {
    return showWhen();
  }

  // If showWhen is an object, check mobile/desktop conditions
  if (typeof showWhen === 'object') {
    const isMobile = isMobileViewport();

    // Check mobile condition
    if (showWhen.mobile !== undefined && isMobile && !showWhen.mobile) {
      return false;
    }

    // Check desktop condition
    if (showWhen.desktop !== undefined && !isMobile && !showWhen.desktop) {
      return false;
    }

    // Check custom condition function
    if (showWhen.condition && typeof showWhen.condition === 'function') {
      return showWhen.condition();
    }

    return true;
  }

  // Default: show the step
  return true;
}

/**
 * Resolve target by data-tutorial-target attribute
 * @param {string} targetKey - The data-tutorial-target value to find
 * @returns {HTMLElement|null}
 */
function resolveTargetByKey(targetKey, { requireVisible = true } = {}) {
  const element = document.querySelector(
    `[data-tutorial-target="${targetKey}"]`
  );
  if (!element) return null;
  if (requireVisible && !isElementVisible(element)) return null;
  return element;
}

/**
 * Resolve target from a step's configuration
 * Supports both targetKey (preferred) and highlightSelector (legacy)
 * @param {Object} step - Tutorial step with targetKey or highlightSelector
 * @returns {HTMLElement|null}
 */
function resolveStepTarget(step, { requireVisible = true } = {}) {
  // First try targetKey if provided
  if (step.targetKey) {
    const target = resolveTargetByKey(step.targetKey, { requireVisible });
    if (target) return target;
  }

  // Fall back to highlightSelector
  if (!step.highlightSelector) return null;

  const selectors = step.highlightSelector.split(',').map((s) => s.trim());

  for (const selector of selectors) {
    // Support data-tutorial-target shorthand: @key
    if (selector.startsWith('@')) {
      const target = resolveTargetByKey(selector.slice(1), { requireVisible });
      if (target) return target;
      continue;
    }

    const el = document.querySelector(selector);
    if (el && (!requireVisible || isElementVisible(el))) {
      return el;
    }
  }

  return null;
}

/**
 * Wait for element to become visible using IntersectionObserver when available
 * @param {HTMLElement} element - Element to observe
 * @param {number} timeoutMs - Maximum wait time
 * @param {number} threshold - Intersection threshold
 * @returns {Promise<boolean>} - True if element becomes visible
 */
function waitForIntersection(element, timeoutMs = 800, threshold = 0.1) {
  if (!element) return Promise.resolve(false);
  if (!('IntersectionObserver' in window)) {
    return Promise.resolve(isElementVisible(element));
  }

  return new Promise((resolve) => {
    let resolved = false;

    const finish = (result) => {
      if (resolved) return;
      resolved = true;
      observer.disconnect();
      clearTimeout(timeoutId);
      resolve(result);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && entry.intersectionRatio >= threshold) {
          finish(true);
        }
      },
      { threshold }
    );

    observer.observe(element);

    const timeoutId = setTimeout(() => {
      finish(isElementVisible(element));
    }, timeoutMs);
  });
}

/**
 * Wait for a target to become available in DOM and visible
 * Uses MutationObserver with timeout for late-rendering targets
 * @param {Object} step - Tutorial step configuration
 * @param {number} timeoutMs - Maximum time to wait in milliseconds
 * @returns {Promise<HTMLElement|null>}
 */
async function resolveTargetWithRetry(step, timeoutMs = 1500) {
  const startTime = performance.now();

  const tryResolve = async () => {
    // Prefer an already-visible target so multi-selector steps (e.g.
    // mobile-only + desktop-only) pick the right element for the viewport.
    const visibleCandidate = resolveStepTarget(step, { requireVisible: true });
    if (visibleCandidate) return visibleCandidate;

    // Fallback: find any matching element and wait for it to become visible
    const candidate = resolveStepTarget(step, { requireVisible: false });
    if (!candidate) return null;

    const remaining = Math.max(0, timeoutMs - (performance.now() - startTime));
    if (remaining <= 0) return null;

    const visible = await waitForIntersection(
      candidate,
      Math.min(remaining, 800)
    );
    return visible && isElementVisible(candidate) ? candidate : null;
  };

  // First, try immediate resolution
  const immediateTarget = await tryResolve();
  if (immediateTarget) return immediateTarget;

  // Set up MutationObserver to wait for target to appear
  return new Promise((resolve) => {
    const startTime = performance.now();
    let resolved = false;
    let checking = false;

    const checkTarget = async () => {
      if (resolved || checking) return;
      checking = true;
      const target = await tryResolve();
      checking = false;
      if (resolved) return;
      if (target) {
        resolved = true;
        observer.disconnect();
        clearTimeout(timeoutId);
        resolve(target);
      }
    };

    const observer = new MutationObserver(() => {
      void checkTarget();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style', 'data-tutorial-target'],
    });

    // Also poll periodically in case mutations are missed
    const pollInterval = setInterval(() => {
      if (resolved || performance.now() - startTime > timeoutMs) {
        clearInterval(pollInterval);
        return;
      }
      void checkTarget();
    }, 100);

    // Timeout fallback
    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        observer.disconnect();
        clearInterval(pollInterval);
        resolve(null);
      }
    }, timeoutMs);

    // Do an initial check
    void checkTarget();
  });
}

/**
 * Check if the mobile drawer is currently open
 * @returns {boolean}
 */
function isMobileDrawerOpen() {
  const paramPanel = document.getElementById('paramPanel');
  return paramPanel && paramPanel.classList.contains('drawer-open');
}

/**
 * A step's target inside the open Customizer but scrolled out of what it
 * shows is brought into view at once, before the step is placed. The
 * resolver looks only for targets on the screen, so one scrolled away (a
 * Tab after typing a value can carry the Presets section off the top) was
 * given up on and its step skipped. On a phone the sheet shows only a few
 * rows, and a target scrolled to smoothly after the card was placed left
 * the card over the sheet and its closing arrow.
 *
 * @param {HTMLElement|null} el - The step's target
 */
function bringIntoCustomizer(el) {
  if (!el || !isParamPanelOpen()) return;
  const panel = document.getElementById('paramPanel');
  if (!panel.contains(el)) return;

  const box = panel.getBoundingClientRect();
  const top = box.top + panel.clientTop;
  const r = el.getBoundingClientRect();
  if (r.top >= top && r.bottom <= top + panel.clientHeight) return;

  // The panel's scroll padding is the strip its sticky header covers.
  scrollTargetIntoContainerView(el, {
    topInset: parseFloat(getComputedStyle(panel).scrollPaddingTop) || 0,
  });
}

/**
 * Check if the param panel is currently visible/accessible
 * Works for both mobile drawer and desktop panel
 * @returns {boolean}
 */
function isParamPanelOpen() {
  const paramPanel = document.getElementById('paramPanel');
  if (!paramPanel) return false;

  if (isMobileViewport()) {
    // Mobile: check for drawer-open class
    return paramPanel.classList.contains('drawer-open');
  } else {
    // Desktop: check that panel is NOT collapsed
    return !paramPanel.classList.contains('collapsed');
  }
}

/**
 * Check if an element is inside the param panel (mobile drawer)
 * @param {HTMLElement} element - Element to check
 * @returns {boolean}
 */
function isInsideParamPanel(element) {
  const paramPanel = document.getElementById('paramPanel');
  return paramPanel && paramPanel.contains(element);
}

/**
 * Open the mobile drawer programmatically
 */
function openMobileDrawer() {
  const toggleBtn = document.getElementById('mobileDrawerToggle');
  if (toggleBtn && isMobileViewport() && !isMobileDrawerOpen()) {
    toggleBtn.click();
  }
}

/**
 * Close the mobile drawer programmatically
 * Used when advancing to a step that targets elements outside the param panel
 */
function closeMobileDrawer() {
  const toggleBtn = document.getElementById('mobileDrawerToggle');
  if (toggleBtn && isMobileViewport() && isMobileDrawerOpen()) {
    toggleBtn.click();
  }
}

/**
 * Open/expand the param panel (works on both mobile and desktop)
 */
function openParamPanel() {
  if (isMobileViewport()) {
    openMobileDrawer();
  } else {
    // Desktop: click the collapse button to expand if collapsed
    const collapseBtn = document.getElementById('collapseParamPanelBtn');
    const paramPanel = document.getElementById('paramPanel');
    if (collapseBtn && paramPanel?.classList.contains('collapsed')) {
      collapseBtn.click();
    }
  }
}

/**
 * Wait for an element's transition to complete
 * @param {HTMLElement} element - Element to watch
 * @param {number} timeout - Fallback timeout in ms
 * @returns {Promise<void>}
 */
function waitForTransition(element, timeout = 500) {
  return new Promise((resolve) => {
    if (!element) {
      resolve();
      return;
    }

    // Check if element has a transition
    const hasTransition =
      getComputedStyle(element).transition !== 'none 0s ease 0s';

    if (!hasTransition) {
      resolve();
      return;
    }

    let resolved = false;

    const handler = (e) => {
      if (e.target === element && !resolved) {
        resolved = true;
        element.removeEventListener('transitionend', handler);
        resolve();
      }
    };

    element.addEventListener('transitionend', handler);

    // Timeout fallback in case transitionend doesn't fire
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        element.removeEventListener('transitionend', handler);
        resolve();
      }
    }, timeout);
  });
}

/**
 * Execute a single ensure action
 * @param {Object} action - Action configuration
 * @returns {Promise<void>}
 */
async function executeEnsureAction(action) {
  switch (action.type) {
    case 'openDrawer': {
      if (!isMobileDrawerOpen() && isMobileViewport()) {
        openMobileDrawer();
        const drawer = document.getElementById('paramPanel');
        await waitForTransition(drawer, 400);
      }
      break;
    }

    case 'closeDrawer': {
      if (isMobileDrawerOpen() && isMobileViewport()) {
        closeMobileDrawer();
        const drawer = document.getElementById('paramPanel');
        await waitForTransition(drawer, 400);
      }
      break;
    }

    case 'openDetails': {
      const details = document.querySelector(action.selector);
      if (details instanceof HTMLDetailsElement && !details.open) {
        details.open = true;
        // Small delay for animation
        await new Promise((r) => setTimeout(r, 100));
      }
      break;
    }

    case 'closeDetails': {
      const details = document.querySelector(action.selector);
      if (details instanceof HTMLDetailsElement && details.open) {
        details.open = false;
        await new Promise((r) => setTimeout(r, 100));
      }
      break;
    }

    case 'expandPanel': {
      const panel = document.querySelector(action.selector);
      const toggle = panel?.querySelector('[aria-expanded]');
      if (toggle && toggle.getAttribute('aria-expanded') === 'false') {
        toggle.click();
        await waitForTransition(panel, 400);
      }
      break;
    }

    case 'collapsePanel': {
      const panel = document.querySelector(action.selector);
      const toggle = panel?.querySelector('[aria-expanded]');
      if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
        toggle.click();
        await waitForTransition(panel, 400);
      }
      break;
    }

    case 'clickIfExpanded': {
      // Click a specific toggle element if its aria-expanded is 'true'.
      // Unlike collapsePanel (which searches *inside* the selector for a toggle),
      // this targets the toggle element directly — needed when a parent container
      // contains multiple aria-expanded elements.
      const el = document.querySelector(action.selector);
      if (el && el.getAttribute('aria-expanded') === 'true') {
        el.click();
        await waitForTransition(
          el.closest('.preview-info-section') || el.parentElement || el,
          400
        );
      }
      break;
    }

    case 'wait': {
      await new Promise((r) => setTimeout(r, action.duration || 300));
      break;
    }

    default:
      console.warn(`Unknown ensure action type: ${action.type}`);
  }
}

/**
 * Execute step preconditions (ensure actions)
 * @param {Object} step - Tutorial step with optional ensure array
 * @returns {Promise<boolean>} - True if all preconditions succeeded
 */
async function ensureStepPreconditions(step) {
  if (!step.ensure || !Array.isArray(step.ensure)) {
    return true;
  }

  try {
    for (const action of step.ensure) {
      await executeEnsureAction(action);
      // Small delay between actions
      await new Promise((r) => setTimeout(r, 50));
    }
    return true;
  } catch (e) {
    console.warn('Tutorial precondition failed:', e);
    announceToScreenReader('Setup for this step failed. Moving to next step.');
    return false;
  }
}

/**
 * Debounce timeout for drawer state change handling
 */
let drawerChangeTimeout = null;

/**
 * Set up observer to watch for drawer state changes during tutorial
 * Handles both mobile drawer (open/close) and desktop panel (collapsed/expanded)
 */
function setupDrawerObserver() {
  clearDrawerObserver();

  const paramPanel = document.getElementById('paramPanel');
  if (!paramPanel) return;

  drawerObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'class') {
        // Handle drawer state changes on both mobile and desktop
        if (isMobileViewport()) {
          // Mobile: watch for 'drawer-open' class
          const isNowOpen = paramPanel.classList.contains('drawer-open');
          handleDrawerStateChange(isNowOpen);
        } else {
          // Desktop: watch for 'collapsed' class
          const isNowOpen = !paramPanel.classList.contains('collapsed');
          handleDrawerStateChange(isNowOpen);
        }
      }
    });
  });

  drawerObserver.observe(paramPanel, {
    attributes: true,
    attributeFilter: ['class'],
  });
}

/**
 * Clear the drawer observer
 */
function clearDrawerObserver() {
  if (drawerObserver) {
    drawerObserver.disconnect();
    drawerObserver = null;
  }
  // Clear any pending debounce
  if (drawerChangeTimeout) {
    clearTimeout(drawerChangeTimeout);
    drawerChangeTimeout = null;
  }
}

/**
 * Handle drawer state change (open or close) during a tutorial step
 * Re-resolves the step target and repositions spotlight accordingly
 * @param {boolean} isNowOpen - Whether the drawer is now open
 */
function handleDrawerStateChange(isNowOpen) {
  // Debounce rapid changes during animation
  if (drawerChangeTimeout) {
    clearTimeout(drawerChangeTimeout);
  }

  drawerChangeTimeout = setTimeout(async () => {
    drawerChangeTimeout = null;

    if (!activeTutorial || !tutorialOverlay) return;

    // Don't interfere while a step is being set up - prevents race conditions
    // that can cause the drawer to open/close in a loop
    if (isSettingUpStep) {
      return;
    }

    const step = activeTutorial.steps[currentStepIndex];
    if (!step.highlightSelector && !step.targetKey) return;
    const stepAtStart = currentStepIndex;
    const watcher = drawerObserver;

    // For steps with completion requirements, block updates after completion to prevent loops
    // This prevents unnecessary re-resolution when the user has already completed the action
    if (step.completion && stepCompleted) {
      return;
    }

    // Wait for drawer animation to complete
    const paramPanel = document.getElementById('paramPanel');
    if (paramPanel) {
      await waitForTransition(paramPanel, 350);
    }

    // The wait can outlast the step. Next pressed inside it brings a new
    // step, which has resolved its own target by now; this callback would
    // hand it the old step's and dock the card over the control it asks for.
    if (currentStepIndex !== stepAtStart || drawerObserver !== watcher) {
      return;
    }

    // Re-resolve the step target - it will pick the first visible target
    // from the selector list (e.g., @collapse-param-panel when open,
    // @mobile-drawer-toggle when closed)
    const newTarget = resolveStepTarget(step, { requireVisible: true });

    if (newTarget) {
      if (newTarget !== currentTarget) {
        // Update to the new visible target and reposition spotlight
        currentTarget = newTarget;
        bringIntoCustomizer(newTarget);
        updateSpotlightAndPosition();
      }
      resetStepRequirement(step);
    } else if (
      !stepCompleted &&
      !isNowOpen &&
      checkIfAnyTargetInsideDrawer(step)
    ) {
      // The user's close wins. Reopening the drawer here, on the grounds that
      // the tutorial needs it, would undo the user's Close inside 500 ms and
      // leave the tour looking dead. Instead the tour points at the way back
      // in, says so, and waits.
      showDrawerRequirement();
    }
  }, 100); // Debounce for 100ms
}

/**
 * Every element a step could point at, visible or not: its targetKey first,
 * then each entry of its highlightSelector list, in the order the resolver
 * would try them.
 * @param {Object} step - Tutorial step
 * @returns {HTMLElement[]}
 */
function stepTargetCandidates(step) {
  const found = [];
  const add = (el) => {
    if (el && !found.includes(el)) found.push(el);
  };

  if (step.targetKey) {
    add(document.querySelector(`[data-tutorial-target="${step.targetKey}"]`));
  }
  if (step.highlightSelector) {
    for (const selector of step.highlightSelector.split(',')) {
      const trimmed = selector.trim();
      add(
        document.querySelector(
          trimmed.startsWith('@')
            ? `[data-tutorial-target="${trimmed.slice(1)}"]`
            : trimmed
        )
      );
    }
  }
  return found;
}

/**
 * Check if any of the step's targets are inside the param panel
 * @param {Object} step - Tutorial step
 * @returns {boolean}
 */
function checkIfAnyTargetInsideDrawer(step) {
  return stepTargetCandidates(step).some(isInsideParamPanel);
}

/**
 * Does this step need the Customizer panel opened before it can be shown?
 *
 * The question is whether the step can be shown at all without opening it,
 * not whether any target lives inside the panel. The step that teaches
 * opening the panel lists `@collapse-param-panel` first for the desktop,
 * where it resolves in the DOM while the panel is shut; opening the panel
 * for it would ring the collapse arrow and teach the opposite of what the
 * card says. On a phone that arrow is hidden and the Customizer button is
 * the target throughout: it opens the sheet and closes it again.
 *
 * If some target is inside the panel but another one outside is already on
 * screen, the step has something to point at and the panel stays as the
 * user left it. That step then arrives with the drawer shut and the ring on
 * the Customizer button, which keeps the ring while the user opens and
 * closes the sheet: the step follows the user through both halves of what
 * it is teaching.
 *
 * A collapsed desktop panel still expands: there the only visible candidate
 * is inside the panel.
 * @param {Object} step - Tutorial step
 * @returns {boolean}
 */
function stepNeedsParamPanelOpen(step) {
  const candidates = stepTargetCandidates(step);
  if (!candidates.some(isInsideParamPanel)) return false;
  return !candidates.some(
    (el) => !isInsideParamPanel(el) && isElementVisible(el)
  );
}

/** What the requirement line says while a step's action is still outstanding. */
const REQUIREMENT_PENDING_TEXT = '↑ Complete the action above to continue';

/**
 * The requirement names the control by the label it wears on the surface
 * the reader is looking at. On a phone the panel is a drawer you open with
 * a button marked Customizer; on a desktop it is a panel you expand.
 */
const DRAWER_REQUIREMENT_TEXT = {
  mobile: 'Open the Customizer to continue.',
  desktop: 'Expand the Customizer to continue.',
};

/**
 * The user closed the Customizer while a step still points inside it.
 * Ring the control that opens it again and say what is needed, instead of
 * reopening the panel against them.
 *
 * The ring only moves if that control is genuinely on screen: a collapsed
 * desktop panel keeps its expand button inside the panel, where the engine's
 * own visibility rule counts it as hidden. The line is shown either way.
 */
function showDrawerRequirement() {
  if (!tutorialOverlay) return;

  const onMobile = isMobileViewport();
  const opener = onMobile
    ? document.getElementById('mobileDrawerToggle')
    : document.getElementById('collapseParamPanelBtn');

  currentTarget = opener && isElementVisible(opener) ? opener : null;
  updateSpotlightAndPosition();

  const wanted = DRAWER_REQUIREMENT_TEXT[onMobile ? 'mobile' : 'desktop'];
  const requirementEl = tutorialOverlay.querySelector('#tutorialRequirement');
  // #tutorialRequirement is role="status" aria-live="polite": writing it IS
  // the announcement, so announcing again would say it twice.
  if (requirementEl && requirementEl.textContent !== wanted) {
    requirementEl.textContent = wanted;
    requirementEl.classList.remove('tutorial-requirement-done');
  }
}

/**
 * Put the requirement line back to whatever this step normally says, after the
 * drawer requirement has been standing in for it.
 * @param {Object} step - Tutorial step
 */
function resetStepRequirement(step) {
  const requirementEl = tutorialOverlay?.querySelector('#tutorialRequirement');
  if (!requirementEl || stepCompleted) return;
  const normal = step.completion ? REQUIREMENT_PENDING_TEXT : '';
  if (requirementEl.textContent !== normal) {
    requirementEl.textContent = normal;
  }
}

/**
 * Check if we should use compact content for tutorials
 * Returns true when viewport is small or zoom is high
 * @returns {boolean}
 */
function shouldUseCompactContent() {
  const viewport = getEffectiveViewport();
  // Use compact content when:
  // - High zoom (scale > 1.25)
  // - Short viewport (height < 520px)
  // - Narrow viewport (width < 400px - mobile portrait)
  return viewport.scale > 1.25 || viewport.height < 520 || viewport.width < 400;
}

/**
 * Get the appropriate content for a tutorial step
 * Returns compact content if available and conditions warrant it
 * @param {Object} step - Tutorial step
 * @returns {string} - HTML content to display
 */
function getStepContent(step) {
  if (step.contentCompact && shouldUseCompactContent()) {
    return step.contentCompact;
  }
  return step.content;
}

/**
 * Modes a tutorial runs in when it does not declare its own homeModes.
 * Tutorials whose targets are Forge chrome break over the Classic DOM, so
 * cross-interface launches go through the consent dialog.
 */
const FORGE_HOME_MODES = ['simplified', 'standard'];

/** Standard-density Classic shows the editor, console and Edit menu. */
const isClassicStandardDensity = () =>
  document.body?.dataset?.classicDensity === 'standard';

/**
 * Tutorial step definitions
 */
const TUTORIALS = {
  intro: {
    id: 'intro',
    title: 'Getting Started',
    forceMode: 'simplified',
    modeVariants: { classic: 'classic-intro' },
    steps: [
      {
        title: 'Welcome!',
        content: `
          <p>This quick tour helps you get comfortable with the interface so you can start customizing right away.</p>
          <ul>
            <li>Find the main panels and drawers</li>
            <li>Change a parameter and see the preview update</li>
            <li>Save a preset and generate a file to download</li>
          </ul>
          <p class="tutorial-hint">Works on desktop and mobile (portrait or landscape).</p>
          <p class="tutorial-hint">Press <kbd>Esc</kbd> to exit anytime.</p>
        `,
        contentCompact: `
          <p>Quick tour of the main controls (~3 min).</p>
          <details class="tutorial-more">
            <summary>What you'll learn</summary>
            <ul>
              <li>Find the Customizer, Preview, Actions</li>
              <li>Change a parameter</li>
              <li>Generate &amp; download</li>
            </ul>
          </details>
          <p class="tutorial-hint"><kbd>Esc</kbd> to exit anytime.</p>
        `,
        position: 'center',
      },
      {
        title: 'The 3 main areas',
        content: `
          <p>The app is organized into three areas:</p>
          <ul>
            <li><strong>Customizer</strong> - change your model’s settings</li>
            <li><strong>Preview</strong> - see the 3D model update</li>
            <li><strong>Actions</strong> - export, share, and other tools</li>
          </ul>
          <p class="tutorial-hint">Next, we’ll find each area in your layout.</p>
        `,
        contentCompact: `
          <p><strong>Customizer</strong> (settings) · <strong>Preview</strong> (3D) · <strong>Actions</strong> (export)</p>
          <p class="tutorial-hint">Next: find each area.</p>
        `,
        position: 'center',
      },
      {
        title: 'Open and close the Customizer',
        content: `
          <p><strong>Customizer</strong> is where you change the model.</p>
          <ul>
            <li><strong>Small screens:</strong> press the highlighted <strong>Customizer</strong> button to open the panel. When it is open, the same button closes it again: its arrow turns to point the way it closes.</li>
            <li><strong>Wide screens:</strong> the Customizer panel is on the left. Use the edge <strong>collapse</strong> button to shrink or expand it.</li>
          </ul>
        `,
        contentCompact: `
          <p>Press the highlighted <strong>Customizer</strong> button to open the panel. Press it again to close it.</p>
        `,
        highlightSelector: '@collapse-param-panel, @mobile-drawer-toggle',
        position: 'right',
      },
      {
        title: 'Expand a parameter group',
        content: `
          <p>Your model’s parameters are organized into <strong>collapsible groups</strong>.</p>
          <p><strong>Try it:</strong> click the <strong>Dimensions</strong> group header to expand it and reveal the sliders inside.</p>
          <p class="tutorial-hint">Each group can be expanded or collapsed independently.</p>
        `,
        contentCompact: `
          <p>Click <strong>Dimensions</strong> to expand the group.</p>
        `,
        highlightSelector:
          '.param-group[data-group-id="Dimensions"] summary, .param-group summary',
        position: 'right',
        lockScroll: true,
        completion: {
          type: 'detailsOpen',
          selector:
            '.param-group[data-group-id="Dimensions"], .param-group:first-of-type',
        },
      },
      {
        title: 'Adjust a parameter',
        content: `
          <p><strong>Try it:</strong> change <strong>Width</strong> and watch the preview update.</p>
          <p class="tutorial-hint">You can drag the slider or type a number.</p>
        `,
        highlightSelector:
          '#param-width, .param-control[data-param-name="width"] input, .param-control[data-param-name="width"]',
        position: 'right',
        lockScroll: true,
        completion: {
          type: 'domEvent',
          // Some models/themes may render the width input with a slightly different structure.
          // Use a comma-separated selector list (querySelector supports this).
          selector:
            '#param-width, #param-width input, .param-control[data-param-name="width"] input[type="range"], .param-control[data-param-name="width"] input',
          event: 'input',
        },
      },
      {
        title: 'See the preview update',
        content: `
          <p>Nice! The <strong>Preview</strong> updates automatically as you change parameters.</p>
          <p class="tutorial-hint">If you don’t see the model yet, click <strong>Generate</strong> later to build the full 3D file.</p>
        `,
        highlightSelector: '@preview-container',
        position: 'left',
        // Collapse the Preview Settings drawer so it doesn't obscure the 3D canvas.
        // The drawer gets its own dedicated step later ("Preview Settings & Info").
        ensure: [{ type: 'clickIfExpanded', selector: '#previewDrawerToggle' }],
      },
      {
        title: 'Save a preset',
        content: `
          <p><strong>Presets</strong> let you save and switch between different configurations.</p>
          <details class="tutorial-more">
            <summary>What each button does</summary>
            <ul>
              <li><strong>Select</strong> (dropdown): pick a saved preset</li>
              <li><strong>Save</strong>: overwrite the current preset with your changes</li>
              <li><strong>Add (+)</strong>: create a new preset from current settings</li>
              <li><strong>Delete (\u2212)</strong>: remove the selected preset</li>
              <li><strong>Import / Export</strong>: share presets as JSON files. You can sort presets or choose to merge or replace all when importing.</li>
            </ul>
          </details>
          <p class="tutorial-hint">Presets are saved in your browser for this model.</p>
        `,
        contentCompact: `
          <p>Use <strong>Presets</strong> to save, load, and share configurations.</p>
          <p class="tutorial-hint">Saved in your browser for this model.</p>
        `,
        // Target the clickable summary so the panel can avoid covering it.
        highlightSelector: '#presetControls summary, @preset-controls',
        position: 'right',
        // Saving a preset is the step, not opening the section it lives in.
        completion: {
          type: 'domEvent',
          selector: 'body',
          event: 'forge:preset-saved',
        },
      },
      {
        title: 'Preview Settings & Info',
        content: `
          <p>Open <strong>Preview Settings &amp; Info</strong> to find:</p>
          <ul>
            <li>Status and progress</li>
            <li>Model dimensions</li>
            <li>Preview quality</li>
            <li><strong>Grid size</strong>: set the grid to match your printer bed. <strong>Try it:</strong> change the width to match your printer.</li>
          </ul>
          <p class="tutorial-hint">You can resize this drawer using the handle. With keyboard: focus the handle, then use arrow keys.</p>
        `,
        contentCompact: `
          <p>Tap to see status, dimensions, and quality settings.</p>
          <p class="tutorial-hint">Set the grid size to match your printer bed!</p>
        `,
        highlightSelector: '@preview-drawer-toggle',
        position: 'left',
      },
      {
        title: 'Sharing and drawings',
        content: `
          <p><strong>Copy Link</strong> sits with the presets: it copies a link that opens this design with your settings.</p>
          <ul>
            <li><strong>Publish</strong> (File menu): a manifest to share this project from your own hosting</li>
            <li><strong>Open a drawing in the editor</strong> (Design menu): an SVG drawing or a photo, opened in the drawing editor</li>
          </ul>
          <p class="tutorial-hint">The File and Design menus show in the Standard view. On a phone, the presets are in the Customizer.</p>
        `,
        highlightSelector: '@preset-controls, @mobile-drawer-toggle',
        position: 'auto',
      },
      {
        title: 'Camera controls',
        content: `
          <p>Use <strong>Camera</strong> controls to rotate, pan, zoom, and reset the view without drag gestures.</p>
          <ul>
            <li><strong>Mobile:</strong> open the Camera drawer in the bottom bar</li>
            <li><strong>Desktop:</strong> open the Camera panel on the right</li>
          </ul>
          <p class="tutorial-hint">If you prefer drag gestures, you can skip these controls for now.</p>
        `,
        highlightSelector: '@mobile-camera-drawer-toggle, @camera-panel-toggle',
        position: 'left',
      },
      {
        title: 'Generate and download your file',
        content: `
          <p>Press the highlighted button to build your file. It says <strong>Generate</strong> the first time, and <strong>Download</strong> once a file is ready.</p>
          <p class="tutorial-hint">The button text also changes with the output format you pick in the Customizer panel.</p>
        `,
        highlightSelector: '@primary-action',
        position: 'top',
        completion: {
          type: 'domEvent',
          selector: '#primaryActionBtn',
          event: 'click',
        },
      },
      {
        title: 'Companion Files & Screenshot Overlay',
        content: `
          <p>Some designs use extra files (like <code>.txt</code> or <code>.svg</code>). When loaded from a ZIP, they appear in the <strong>Companion Files</strong> section.</p>
          <p>If a <code>screenshot.png</code> file is included, the app automatically loads it as a <strong>reference overlay</strong> behind your keyguard in the 3D preview, helping you align openings with tablet buttons.</p>
          <p class="tutorial-hint">You can also add files manually using the <strong>Add File</strong> button.</p>
        `,
        contentCompact: `
          <p>Extra files (<code>.txt</code>, <code>.png</code>) appear under <strong>Companion Files</strong>. Screenshots auto-load as reference overlays.</p>
        `,
        highlightSelector:
          '[data-tutorial-target="companion-files"], #projectFilesControls summary',
        position: 'right',
        // Shown only where the section is on screen: Simplified keeps it in
        // the page but hidden, and a step the layout hides takes no number.
        showWhen: {
          condition: () =>
            isElementVisible(document.getElementById('projectFilesControls')),
        },
      },
      {
        title: 'Close the Customizer (mobile)',
        content: `
          <p>On mobile, the <strong>Customizer</strong> drawer sits on top of the app.</p>
          <p class="tutorial-hint">Close it now so you can reach the top buttons like <strong>Help</strong>.</p>
        `,
        highlightSelector: '@mobile-drawer-toggle',
        position: 'right',
        showWhen: { mobile: true, desktop: false },
        completion: {
          type: 'domEvent',
          selector: '#mobileDrawerToggle',
          event: 'click',
        },
      },
      {
        title: 'Help & Examples',
        content: `
          <p>Use <strong>Help</strong> for built-in documentation, examples, and explanations of features.</p>
          <p class="tutorial-hint">Click it now to open the Features Guide.</p>
        `,
        highlightSelector: '@features-guide',
        position: 'left',
        completion: {
          type: 'modalOpen',
          selector: '#featuresGuideModal',
          trigger: '#featuresGuideBtn',
        },
      },
      {
        title: 'Features Guide',
        content: `
          <p>The <strong>Features Guide</strong> is a great place to learn more when you get stuck.</p>
          <p class="tutorial-hint"><strong>Close the modal</strong> (click X or press <kbd>Esc</kbd>) to continue.</p>
        `,
        highlightSelector: '@features-guide-modal',
        position: 'bottom',
        completion: { type: 'modalClose', selector: '#featuresGuideModal' },
      },
      // The Simplified/Standard teaching sits near the end, so nothing the
      // tour shows depends on the choice made in it. The step invites the press
      // and never requires it, so there is no completion gate. The tour
      // survives the press, which is what makes teaching it by pressing safe.
      {
        title: 'Simplified or Standard',
        content: `
          <p>This switch controls how much of the interface is shown. <strong>Simplified</strong> keeps the essentials. <strong>Standard</strong> shows more tools. Press it now to see the difference. The tour stays open, and nothing is lost when you switch.</p>
        `,
        highlightSelector: '#uiModeToggle',
        position: 'bottom',
        showWhen: {
          condition: () => {
            const el = document.getElementById('uiModeToggle');
            return !!el && !el.classList.contains('hidden');
          },
        },
        skipReason: 'not available right now',
      },
      {
        title: "You're ready!",
        content: `
          <p>You now know where the main controls are and how to complete the basic workflow.</p>
          <p><strong>Next steps:</strong></p>
          <ul>
            <li>Try a different example from the Main Page</li>
            <li>Upload your own <code>.scad</code> or <code>.zip</code> project</li>
            <li>Save designs and use Import / Export to share your work</li>
            <li>Set the grid to match your printer bed in Preview Settings</li>
            <li>Don't need a parameter group right now? Hide it with the button on the group header.</li>
          </ul>
        `,
        position: 'center',
      },
      // The tour ends by naming the way back, because the way
      // back people reached for was the browser's Back button, and it closed
      // the app.
      {
        title: 'Back to the Main Page',
        content: `
          <p>You are done here. The <strong>Main Page</strong> button in the top left corner takes you back to your projects. Use it instead of the browser's Back button, which closes the app. If you press that by accident, the app asks first.</p>
          <p class="tutorial-hint">Press <strong>Finish</strong> to end this tour.</p>
        `,
        highlightSelector: '@clear-file',
        position: 'bottom',
      },
    ],
  },
  'classic-intro': {
    id: 'classic-intro',
    title: 'Classic Getting Started',
    homeModes: ['classic'],
    steps: [
      {
        title: 'Welcome to Classic!',
        content: `
          <p>This quick tour shows you around the Classic interface, which looks and works like the OpenSCAD desktop app.</p>
          <ul>
            <li>See where menus, tools, and parameters live</li>
            <li>Change a parameter and watch the 3D view update</li>
            <li>Render your model and export a file</li>
          </ul>
          <p class="tutorial-hint">Press <kbd>Esc</kbd> to exit at any time.</p>
        `,
        position: 'center',
      },
      {
        title: 'The Classic layout',
        content: `
          <p>Classic puts everything in a fixed place, like the desktop app:</p>
          <ul>
            <li><strong>Menu bar</strong> and <strong>toolbar</strong> along the top</li>
            <li><strong>3D view</strong> in the middle</li>
            <li><strong>Customizer</strong> with your parameters on the right</li>
            <li><strong>Status bar</strong> along the bottom</li>
          </ul>
          <p class="tutorial-hint">The Standard view also shows the code editor and console on the left.</p>
        `,
        position: 'center',
      },
      {
        title: 'The menu bar',
        content: `
          <p>The menus group every command: <strong>File</strong>, <strong>Design</strong>, <strong>View</strong>, and <strong>Help</strong>. The Standard view adds <strong>Edit</strong> and <strong>Window</strong>.</p>
          <p class="tutorial-hint">Keyboard: press <kbd>Alt</kbd> plus the underlined letter to open a menu.</p>
        `,
        highlightSelector: '#toolbarMenuBar',
        position: 'bottom',
      },
      {
        title: 'The toolbar',
        content: `
          <p>The toolbar holds the everyday buttons: <strong>Preview</strong> (F5), <strong>Render</strong> (F6), and export to <strong>STL</strong> or <strong>DXF</strong>. The gear button shows or hides the Customizer.</p>
          <p class="tutorial-hint">The Standard view adds New, Open, and Save.</p>
        `,
        highlightSelector: '#classicToolbar',
        position: 'bottom',
      },
      {
        title: 'The Customizer',
        content: `
          <p>The <strong>Customizer</strong> holds your model's parameters, organized into groups. This is where most customizing happens.</p>
          <p class="tutorial-hint">Automatic Preview is on by default, so changes show up in the 3D view right away.</p>
        `,
        highlightSelector: '#paramPanel',
        position: 'left',
      },
      {
        title: 'Expand a parameter group',
        content: `
          <p>Your model’s parameters are organized into <strong>collapsible groups</strong>.</p>
          <p><strong>Try it:</strong> click the <strong>Dimensions</strong> group header to open it and see the sliders inside.</p>
        `,
        highlightSelector:
          '.param-group[data-group-id="Dimensions"] summary, .param-group summary',
        position: 'left',
        lockScroll: true,
        completion: {
          type: 'detailsOpen',
          selector:
            '.param-group[data-group-id="Dimensions"], .param-group:first-of-type',
        },
      },
      {
        title: 'Adjust a parameter',
        content: `
          <p><strong>Try it:</strong> change <strong>Width</strong> and watch the 3D view update.</p>
          <p class="tutorial-hint">You can drag the slider or type a number.</p>
        `,
        highlightSelector:
          '#param-width, .param-control[data-param-name="width"] input, .param-control[data-param-name="width"]',
        position: 'left',
        lockScroll: true,
        completion: {
          type: 'domEvent',
          selector:
            '#param-width, #param-width input, .param-control[data-param-name="width"] input[type="range"], .param-control[data-param-name="width"] input',
          event: 'input',
        },
      },
      {
        title: 'The 3D view',
        content: `
          <p>The 3D view shows your model with the same colors and axes as the desktop app. Drag to rotate, scroll to zoom.</p>
          <p class="tutorial-hint">The view updates automatically after each parameter change.</p>
        `,
        highlightSelector: '@preview-container',
        position: 'auto',
      },
      {
        title: 'Presets',
        content: `
          <p>The <strong>Presets</strong> row saves your favorite parameter setups. Pick one from the list, or use the plus button to save the current settings as a new preset.</p>
          <p class="tutorial-hint">Presets are stored in your browser for this model.</p>
        `,
        highlightSelector: '#classicPresetRow',
        position: 'left',
      },
      {
        title: 'Render and export',
        content: `
          <p><strong>Preview</strong> (F5) is fast and approximate. <strong>Render</strong> (F6) builds the exact solid, ready to export.</p>
          <p><strong>Try it:</strong> click <strong>Render</strong> in the toolbar.</p>
          <p class="tutorial-hint">When it finishes, Export as STL or Export as DXF saves your file.</p>
        `,
        highlightSelector: '#classicTbRenderBtn',
        position: 'bottom',
        completion: {
          type: 'domEvent',
          selector: '#classicTbRenderBtn',
          event: 'click',
        },
      },
      {
        title: 'Camera controls',
        content: `
          <p>The <strong>camera bar</strong> rotates, zooms, and resets the view with buttons. No dragging needed.</p>
          <p class="tutorial-hint">A game controller steers the camera too, and moves through the controls (Preferences &gt; Controller).</p>
        `,
        highlightSelector: '#classicCameraBar',
        position: 'top',
      },
      {
        title: 'Stow panels for space',
        content: `
          <p>The <strong>double-arrow</strong> buttons stow a panel onto the edge of the window. Click the tab on the rail to bring it back.</p>
          <p class="tutorial-hint">Try stowing the Customizer when you want a bigger 3D view.</p>
        `,
        highlightSelector: '[data-classic-stow-field="right-top"]',
        position: 'left',
      },
      {
        title: 'The editor',
        content: `
          <p>The <strong>editor</strong> shows your model's OpenSCAD code. Your edits flow into the next Preview or Render.</p>
          <p class="tutorial-hint">The editor toolbar has file and text tools, plus 3D Print.</p>
        `,
        highlightSelector: '#classicEditorSlot',
        position: 'right',
        showWhen: isClassicStandardDensity,
        skipReason: 'not available in this view',
      },
      {
        title: 'Console and Error-Log',
        content: `
          <p>Render messages appear in the <strong>Console</strong>. Problems show up in the <strong>Error-Log</strong> beside it, with the line number when known.</p>
        `,
        highlightSelector: '#classicConsoleSlot',
        position: 'top',
        showWhen: isClassicStandardDensity,
        skipReason: 'not available in this view',
      },
      {
        title: 'Preferences',
        content: `
          <p>Open <strong>Edit</strong>, then <strong>Preferences</strong> to tune Classic: color schemes for the 3D view, editor settings, and more.</p>
          <p class="tutorial-hint">Color schemes preview live in the 3D view while the dialog is open.</p>
        `,
        highlightSelector: '#toolbarMenuBar',
        position: 'bottom',
        showWhen: isClassicStandardDensity,
        skipReason: 'not available in this view',
      },
      // The Classic half of the Simplified/Standard teaching. It sits after
      // the editor, console and Preferences steps deliberately: those three
      // only appear in the Standard view, so a user who chooses Simplified
      // here has already been shown what the extra panes hold.
      {
        title: 'Simplified or Standard',
        content: `
          <p>This switch controls the Classic view. <strong>Simplified</strong> shows the Customizer and the 3D view. <strong>Standard</strong> adds the code editor and console, like the desktop app. Press it now to see the difference. The tour stays open, and you can switch back at any time.</p>
        `,
        highlightSelector: '#classicDensityToggle',
        position: 'bottom',
      },
      {
        title: "You're ready!",
        content: `
          <p>That's the Classic tour.</p>
          <ul>
            <li>The <strong>A. Forge</strong> button returns you to the Assistive Forge interface</li>
            <li>Run this tour again anytime from the Main Page</li>
          </ul>
          <p class="tutorial-hint">Happy modeling!</p>
        `,
        position: 'center',
      },
      // The Classic half of the same ending. One DOM node carries
      // the Main Page button in both interfaces, so both tours point at it.
      {
        title: 'Back to the Main Page',
        content: `
          <p>You are done here. The <strong>Main Page</strong> button in the top left corner takes you back to your projects. Use it instead of the browser's Back button, which closes the app. If you press that by accident, the app asks first.</p>
          <p class="tutorial-hint">Press <strong>Finish</strong> to end this tour.</p>
        `,
        highlightSelector: '@clear-file',
        position: 'bottom',
      },
    ],
  },
  // The welcome-page tour. No project loads, no mode is forced; the Classic
  // variant resolves through modeVariants exactly the way intro/classic-intro
  // pair up, so the registry records both as the 'welcome' family.
  // surface: 'welcome' arms the surface-flip close guard.
  welcome: {
    id: 'welcome',
    title: 'Main Page Tour',
    surface: 'welcome',
    modeVariants: { classic: 'welcome-classic' },
    steps: [
      {
        title: 'Welcome to the Forge!',
        content: `
          <p>This short tour shows you what each part of this Main Page does. Everything runs in your browser. There is nothing to install, and your designs stay on your device.</p>
          <p class="tutorial-hint">About 2 minutes. Press <kbd>Esc</kbd> to exit at any time.</p>
        `,
        position: 'center',
      },
      {
        title: 'Keyboard shortcuts',
        content: `
          <p>The <strong>Keys</strong> button lists every keyboard shortcut. The whole app works without a mouse, and this list is the fastest way to learn the keys.</p>
        `,
        highlightSelector: '#shortcutsToggle',
        position: 'bottom',
      },
      // No Simplified/Standard step here: it belongs to the box tour, where
      // pressing the switch does something; on the welcome surface it changes
      // nothing a user can see.
      {
        title: 'High contrast',
        content: `
          <p>The <strong>HC</strong> button turns on a high-contrast look that keeps text, borders, and focus rings easy to see. It applies everywhere in the app, including this page.</p>
        `,
        highlightSelector: '@contrast-toggle',
        position: 'bottom',
      },
      {
        title: 'Light or dark',
        content: `
          <p>This button switches between the light and dark themes. Your choice is remembered for next time.</p>
        `,
        highlightSelector: '@theme-toggle',
        position: 'bottom',
      },
      {
        title: 'Help and examples',
        content: `
          <p><strong>Help</strong> opens the Features Guide with built-in documentation and examples. If you are ever stuck, start there.</p>
        `,
        highlightSelector: '@features-guide',
        position: 'bottom',
      },
      {
        title: 'Open or start a project',
        content: `
          <p>Projects begin here. Drop a <code>.scad</code> model, a <code>.zip</code> project, or a project folder onto the drop zone, or click it to browse. Below it, <strong>Open Project Folder</strong> opens a folder from your computer, and <strong>Start New Project</strong> begins a blank project.</p>
          <p class="tutorial-hint">Different from desktop OpenSCAD: the app loads a copy into the browser, and your original files stay untouched.</p>
        `,
        highlightSelector: '@welcome-open-panel',
        position: 'right',
      },
      {
        title: 'Saved Projects',
        content: `
          <p>Projects you save appear here, organized into folders. Different from desktop OpenSCAD: saved projects live in this browser's storage, not as files on your disk. Clearing browser data can remove them, so use <strong>Export</strong> to download a backup file of every project. <strong>Import</strong> restores from a backup.</p>
        `,
        highlightSelector: '#savedProjectsPanel',
        position: 'left',
      },
      {
        title: 'Import Folder',
        content: `
          <p><strong>Import Folder</strong> copies a whole project folder from your computer into Saved Projects, including the extra files a model includes or uses.</p>
        `,
        highlightSelector: '#importFolderBtn',
        position: 'left',
        showWhen: {
          condition: () => {
            const el = document.getElementById('importFolderBtn');
            return !!el && !el.hidden;
          },
        },
        skipReason: 'not available in this browser',
      },
      {
        title: 'Connect Folder',
        content: `
          <p><strong>Connect Folder</strong> links a folder on your disk to the app, so changes flow both ways while it is connected. This is the closest match to how desktop OpenSCAD works with files. It is available in Chromium browsers such as Chrome and Edge.</p>
        `,
        highlightSelector: '#connectFolderBtn',
        position: 'left',
        showWhen: {
          condition: () => {
            const el = document.getElementById('connectFolderBtn');
            return !!el && !el.hidden;
          },
        },
        skipReason: 'not available in this browser',
      },
      {
        title: 'Charm Designer',
        content: `
          <p>This card holds a ready-made creative project: wearable charms, pendants, and logo plates with raised or engraved icons. Pick a shape from the <strong>Shape</strong> menu, and the button opens it as a full project. This tour stays on the Main Page, so we will not open it now.</p>
        `,
        highlightSelector: '@charm-card',
        position: 'top',
      },
      {
        title: 'Braille Card Designer',
        content: `
          <p>This card makes 3D-printable braille. Type text and get cards, charms, or two-part tactile signs. Braille translation runs on your device, so nothing you type leaves it. When you are ready, the <strong>Open Braille Card Designer</strong> button starts it.</p>
        `,
        highlightSelector: '@braille-card',
        position: 'top',
      },
      {
        title: 'Clear Cache',
        content: `
          <p>This button opens a dialog that frees the browser storage the app uses. It keeps your saved projects unless you untick the box that keeps them, and exporting a backup first is always the safe move. You do not need it now. If you open it to look, press <strong>Cancel</strong> to come back to this tour.</p>
        `,
        highlightSelector: '#clearStorageBtn',
        position: 'top',
      },
      {
        title: 'Your next step',
        content: `
          <p>That is the whole Main Page. When you are ready to build something, the <strong>Beginners Start Here</strong> card loads a simple example and walks you through changing parameters and generating your first file. The <strong>Main Page</strong> button in the top left corner brings you back here from a project. Press <strong>Finish</strong> to end this tour.</p>
        `,
        highlightSelector: '@beginners-card',
        position: 'top',
      },
    ],
  },
  'welcome-classic': {
    id: 'welcome-classic',
    title: 'Classic Main Page Tour',
    surface: 'welcome',
    homeModes: ['classic'],
    steps: [
      {
        title: 'Welcome to Classic!',
        content: `
          <p>This short tour shows you what each part of the Main Page does. You are in the Classic layout, which looks and works like the OpenSCAD desktop app once a project is open. Everything runs in your browser, and there is nothing to install.</p>
          <p class="tutorial-hint">About 2 minutes. Press <kbd>Esc</kbd> to exit at any time.</p>
        `,
        position: 'center',
      },
      {
        title: 'Keyboard shortcuts',
        content: `
          <p>The <strong>Keys</strong> button lists every keyboard shortcut. The whole app works without a mouse, and this list is the fastest way to learn the keys.</p>
        `,
        highlightSelector: '#shortcutsToggle',
        position: 'bottom',
      },
      // No Simplified/Standard step here either: it belongs to the Classic box
      // tour, for the same reason as the Forge welcome tour above.
      {
        title: 'Open or start a project',
        content: `
          <p>Projects begin here. Drop a <code>.scad</code> model, a <code>.zip</code> project, or a project folder onto the drop zone, or click it to browse. Below it, <strong>Open Project Folder</strong> opens a folder from your computer, and <strong>Start New Project</strong> begins a blank project.</p>
          <p class="tutorial-hint">Different from desktop OpenSCAD: the app loads a copy into the browser, and your original files stay untouched.</p>
        `,
        highlightSelector: '@welcome-open-panel',
        position: 'right',
      },
      {
        title: 'Saved Projects',
        content: `
          <p>Projects you save appear here, organized into folders. Different from desktop OpenSCAD: saved projects live in this browser's storage, not as files on your disk. Clearing browser data can remove them, so use <strong>Export</strong> to download a backup file of every project. <strong>Import</strong> restores from a backup.</p>
        `,
        highlightSelector: '#savedProjectsPanel',
        position: 'left',
      },
      {
        title: 'Import Folder',
        content: `
          <p><strong>Import Folder</strong> copies a whole project folder from your computer into Saved Projects, including the extra files a model includes or uses.</p>
        `,
        highlightSelector: '#importFolderBtn',
        position: 'left',
        showWhen: {
          condition: () => {
            const el = document.getElementById('importFolderBtn');
            return !!el && !el.hidden;
          },
        },
        skipReason: 'not available in this browser',
      },
      {
        title: 'Connect Folder',
        content: `
          <p><strong>Connect Folder</strong> links a folder on your disk to the app, so changes flow both ways while it is connected. This is the closest match to how desktop OpenSCAD works with files. It is available in Chromium browsers such as Chrome and Edge.</p>
        `,
        highlightSelector: '#connectFolderBtn',
        position: 'left',
        showWhen: {
          condition: () => {
            const el = document.getElementById('connectFolderBtn');
            return !!el && !el.hidden;
          },
        },
        skipReason: 'not available in this browser',
      },
      {
        title: 'Charm Designer',
        content: `
          <p>This card holds a ready-made creative project: wearable charms, pendants, and logo plates with raised or engraved icons. Pick a shape from the <strong>Shape</strong> menu, and the button opens it as a full project. This tour stays on the Main Page, so we will not open it now.</p>
        `,
        highlightSelector: '@charm-card',
        position: 'top',
      },
      {
        title: 'Braille Card Designer',
        content: `
          <p>This card makes 3D-printable braille. Type text and get cards, charms, or two-part tactile signs. Braille translation runs on your device, so nothing you type leaves it. When you are ready, the <strong>Open Braille Card Designer</strong> button starts it.</p>
        `,
        highlightSelector: '@braille-card',
        position: 'top',
      },
      {
        title: 'Clear Cache',
        content: `
          <p>This button opens a dialog that frees the browser storage the app uses. It keeps your saved projects unless you untick the box that keeps them, and exporting a backup first is always the safe move. You do not need it now. If you open it to look, press <strong>Cancel</strong> to come back to this tour.</p>
        `,
        highlightSelector: '#clearStorageBtn',
        position: 'top',
      },
      {
        title: 'Your next step',
        content: `
          <p>That is the whole Main Page. When you are ready to build something, the <strong>Beginners Start Here</strong> card loads a simple example and starts the Classic tour of the project screen. The <strong>Main Page</strong> button in the top left corner brings you back here from a project. Press <strong>Finish</strong> to end this tour.</p>
        `,
        highlightSelector: '@beginners-card',
        position: 'top',
      },
    ],
  },
  makers: {
    id: 'makers',
    title: 'Advanced Features',
    steps: [
      {
        title: 'Welcome, Maker!',
        content: `
          <p>This tour covers:</p>
          <ul>
            <li>Library bundles (MCAD, BOSL2)</li>
            <li>Multi-file ZIP projects</li>
            <li>Export formats</li>
          </ul>
          <p class="tutorial-hint">Press <kbd>Esc</kbd> to exit anytime.</p>
        `,
        position: 'center',
      },
      {
        title: 'Libraries Panel',
        content: `
          <p>Click <strong>Libraries</strong> to enable bundles your model needs.</p>
          <p class="tutorial-hint">MCAD, BOSL2, NopSCADlib, dotSCAD available.</p>
        `,
        highlightSelector: '@library-controls',
        position: 'right',
        completion: {
          type: 'detailsOpen',
          selector: '#libraryControls details',
        },
      },
      {
        title: 'Multi-file Projects',
        content: `
          <p>Upload a <strong>ZIP file</strong> with multiple .scad files.</p>
          <p class="tutorial-hint">Dependencies are auto-detected.</p>
        `,
        highlightSelector: '@clear-file',
        position: 'left',
      },
      {
        title: 'Output Formats',
        content: `
          <p><strong>Try it:</strong> Select a different output format.</p>
          <ul>
            <li><strong>STL</strong> - 3D printing</li>
            <li><strong>AMF</strong> - Multi-material, carries color</li>
            <li><strong>OBJ</strong> - Rendering/animation</li>
          </ul>
        `,
        highlightSelector: '@output-format',
        position: 'top',
        completion: {
          type: 'domEvent',
          selector: '#outputFormat',
          event: 'change',
        },
      },
      {
        title: 'Import Presets',
        content: `
          <p>In <strong>Import / Export Presets</strong>, you can choose how imported presets are merged:</p>
          <ul>
            <li><strong>Merge</strong>: add imports alongside your existing presets</li>
            <li><strong>Replace all</strong>: delete existing presets and start fresh from the import file</li>
          </ul>
          <p class="tutorial-hint">Replace mode always asks for confirmation before deleting.</p>
        `,
        contentCompact: `
          <p>Import presets in <strong>Merge</strong> or <strong>Replace all</strong> mode.</p>
        `,
        highlightSelector: '#presetControls summary, @preset-controls',
        position: 'right',
      },
      {
        title: 'All Set!',
        content: `
          <p>You're ready for advanced OpenSCAD workflows.</p>
          <p class="tutorial-hint">Check Help → Libraries for details.</p>
        `,
        position: 'center',
      },
    ],
  },
  'keyboard-only': {
    id: 'keyboard-only',
    title: 'Keyboard Navigation',
    steps: [
      {
        title: 'Keyboard-First Design',
        content: `
          <p>Complete the full workflow without a mouse:</p>
          <ul>
            <li><kbd>Tab</kbd> - Move between controls</li>
            <li><kbd>Arrow keys</kbd> - Adjust sliders</li>
            <li><kbd>Esc</kbd> - Close dialogs</li>
          </ul>
        `,
        position: 'center',
      },
      {
        title: 'Skip Link',
        content: `
          <p>Press <kbd>Tab</kbd> at page top to reveal "Skip to content".</p>
          <p class="tutorial-hint">Bypasses the header directly to controls.</p>
        `,
        highlightSelector: '.skip-link',
        position: 'bottom',
      },
      {
        title: 'Navigate the Customizer',
        content: `
          <p><strong>Try it:</strong> Press <kbd>Tab</kbd> to reach a parameter, then use <kbd>Arrow keys</kbd> to adjust.</p>
          <p class="tutorial-hint"><kbd>Ctrl+Z</kbd> to undo, <kbd>Ctrl+Shift+Z</kbd> to redo.</p>
        `,
        highlightSelector: '#parametersContainer',
        position: 'right',
        lockScroll: true,
        completion: {
          type: 'domEvent',
          selector: '#parametersContainer',
          event: 'input',
        },
      },
      {
        title: 'Modal Navigation',
        content: `
          <p>Dialogs trap focus inside. Press <kbd>Esc</kbd> to close and return to your previous location.</p>
        `,
        position: 'center',
      },
      {
        title: 'Hide Parameter Groups',
        content: `
          <p>If a parameter group isn't relevant to your current task, you can hide it:</p>
          <ul>
            <li>Tab to a group header, then Tab again to reach the <strong>Hide group</strong> button (×).</li>
            <li>Press <kbd>Space</kbd> or <kbd>Enter</kbd> to hide the group.</li>
            <li>A <strong>Show all</strong> button appears at the bottom of the panel. Tab to it and press <kbd>Enter</kbd> to restore all groups.</li>
          </ul>
          <p class="tutorial-hint">Hidden groups are remembered per model.</p>
        `,
        contentCompact: `
          <p>Tab to a group's × button to hide it. Tab to "Show all" to restore.</p>
        `,
        highlightSelector: '#parametersContainer',
        position: 'right',
      },
      {
        title: "You're Set!",
        content: `
          <p>All features work with keyboard only.</p>
          <p class="tutorial-hint">See Keyguard Workflow Guide for full reference.</p>
        `,
        position: 'center',
      },
    ],
  },
  'low-vision': {
    id: 'low-vision',
    title: 'Visual Accessibility',
    steps: [
      {
        title: 'Visual Features',
        content: `
          <p>This tour covers:</p>
          <ul>
            <li>High contrast mode</li>
            <li>Theme switching</li>
            <li>Large touch targets</li>
          </ul>
        `,
        position: 'center',
      },
      {
        title: 'High Contrast',
        content: `
          <p><strong>Try it:</strong> Click the <strong>HC button</strong> to toggle high contrast mode.</p>
          <p class="tutorial-hint">Increases borders and contrast ratios.</p>
        `,
        highlightSelector: '@contrast-toggle',
        position: 'bottom',
        completion: {
          type: 'domEvent',
          selector: '#contrastToggle',
          event: 'click',
        },
      },
      {
        title: 'Theme Toggle',
        content: `
          <p><strong>Try it:</strong> Click the <strong>theme button</strong> (sun/moon) to switch light/dark.</p>
        `,
        highlightSelector: '@theme-toggle',
        position: 'bottom',
        completion: {
          type: 'domEvent',
          selector: '#themeToggle',
          event: 'click',
        },
      },
      {
        title: 'Touch Targets',
        content: `
          <p>All buttons meet <strong>44×44px minimum</strong> touch target size (WCAG AAA).</p>
          <p class="tutorial-hint">Easy to tap even with reduced precision.</p>
        `,
        position: 'center',
      },
      {
        title: 'Ready!',
        content: `
          <p>The app supports Windows High Contrast Mode and system preferences.</p>
        `,
        position: 'center',
      },
    ],
  },
  'voice-input': {
    id: 'voice-input',
    title: 'Voice Control',
    steps: [
      {
        title: 'Voice-Friendly Design',
        content: `
          <p>All controls have <strong>unique, speakable names</strong> for voice recognition software.</p>
        `,
        position: 'center',
      },
      {
        title: 'Button Commands',
        content: `
          <p>Say the button label:</p>
          <ul>
            <li>"Click Generate"</li>
            <li>"Click Reset"</li>
            <li>"Click Help"</li>
          </ul>
        `,
        highlightSelector: '@primary-action',
        position: 'top',
      },
      {
        title: 'Parameter Controls',
        content: `
          <p>Each parameter has a unique label:</p>
          <ul>
            <li>"Click Width" (focus input)</li>
            <li>"Set Width to 50"</li>
          </ul>
        `,
        highlightSelector: '@parameters-container',
        position: 'right',
      },
      {
        title: 'Navigation',
        content: `
          <ul>
            <li>"Scroll down/up"</li>
            <li>"Click Help"</li>
            <li>"Click Clear" (return to start)</li>
          </ul>
        `,
        position: 'center',
      },
      {
        title: 'All Set!',
        content: `
          <p>All controls are voice-friendly with stable, speakable names.</p>
        `,
        position: 'center',
      },
    ],
  },
  'screen-reader': {
    id: 'screen-reader',
    title: 'Screen Reader Guide',
    steps: [
      {
        title: 'Screen Reader Support',
        content: `
          <p>This tour covers:</p>
          <ul>
            <li>Status announcements</li>
            <li>ARIA landmarks</li>
            <li>Help locations</li>
          </ul>
        `,
        position: 'center',
      },
      {
        title: 'Expand Parameter Groups',
        content: `
          <p>Your model’s parameters are in collapsible <strong>disclosure widgets</strong> (details/summary).</p>
          <p><strong>Try it:</strong> Navigate to <strong>Dimensions</strong> and activate it to expand.</p>
          <p class="tutorial-hint">Press Enter or Space on the group header to toggle.</p>
        `,
        highlightSelector:
          '.param-group[data-group-id="Dimensions"] summary, .param-group summary',
        position: 'right',
        lockScroll: true,
        completion: {
          type: 'detailsOpen',
          selector:
            '.param-group[data-group-id="Dimensions"], .param-group:first-of-type',
        },
      },
      {
        title: 'Status Announcements',
        content: `
          <p>The <strong>Status area</strong> announces all operations automatically.</p>
          <p><strong>Try it:</strong> Adjust <strong>Width</strong> and listen for the announcement.</p>
        `,
        highlightSelector:
          '#statusArea, .param-control[data-param-name="width"], #param-width',
        position: 'auto',
        lockScroll: true,
        completion: {
          type: 'domEvent',
          selector:
            '#param-width, #param-width input, .param-control[data-param-name="width"] input[type="range"], .param-control[data-param-name="width"] input',
          event: 'input',
        },
      },
      {
        title: 'ARIA Landmarks',
        content: `
          <p>Navigate quickly with landmarks:</p>
          <ul>
            <li><strong>Main</strong> - Main content</li>
            <li><strong>Region</strong> - Customizer, Preview</li>
          </ul>
          <p class="tutorial-hint">NVDA: D key, VoiceOver: VO+U</p>
        `,
        position: 'center',
      },
      {
        title: 'Help Button',
        content: `
          <p>The Help button is always in the same location (top-right of Preview).</p>
        `,
        highlightSelector: '@features-guide',
        position: 'left',
      },
      {
        title: 'Console Warnings',
        content: `
          <p>When a model references a missing file (e.g. <code>include &lt;missing.txt&gt;</code>), the <strong>Console</strong> panel in the Customizer area announces the warning.</p>
          <ul>
            <li>A badge count appears on the Console button</li>
            <li>The live region announces the warning to your screen reader</li>
            <li>Filter by Warnings to see only relevant messages</li>
          </ul>
        `,
        contentCompact: `
          <p>Missing-file warnings appear in the <strong>Console</strong> panel with a live region announcement.</p>
        `,
        highlightSelector:
          '[data-tutorial-target="console-panel"], .console-panel-btn',
        position: 'right',
      },
      {
        title: 'Ready!',
        content: `
          <p>All features have proper ARIA labels and live region announcements.</p>
          <p class="tutorial-hint">New: sort-change, group-hide, and grid-size updates are all announced via live regions.</p>
        `,
        position: 'center',
      },
    ],
  },
};

// ============================================================================
// State Persistence Functions
// ============================================================================

/**
 * Save tutorial progress to sessionStorage
 * @param {number} stepIndex - Current step index
 */
function saveTutorialProgress(stepIndex) {
  try {
    const progress = {
      tutorialId: activeTutorial?.id,
      stepIndex,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify(progress));
  } catch (e) {
    console.warn('Failed to save tutorial progress:', e);
  }
}

/**
 * Load tutorial progress from sessionStorage
 * @returns {{tutorialId: string, stepIndex: number}|null}
 */
function loadTutorialProgress() {
  try {
    const saved = sessionStorage.getItem(TUTORIAL_STORAGE_KEY);
    if (!saved) return null;

    const progress = JSON.parse(saved);

    // Check if progress is still valid (within 24 hours)
    if (Date.now() - progress.timestamp > TUTORIAL_PROGRESS_EXPIRY_MS) {
      clearTutorialProgress();
      return null;
    }

    return progress;
  } catch (e) {
    console.warn('Failed to load tutorial progress:', e);
    return null;
  }
}

/**
 * Clear saved tutorial progress
 */
function clearTutorialProgress() {
  try {
    sessionStorage.removeItem(TUTORIAL_STORAGE_KEY);
  } catch (e) {
    console.warn('Failed to clear tutorial progress:', e);
  }
}

// ============================================================================
// Persistent tutorial registry
// ============================================================================
// localStorage, unlike the sessionStorage step progress above: completing a
// tutorial erases its progress record, so "was this ever opened/completed?"
// needs its own persistent answer. The welcome spotlight and the
// welcome tour's chaining both read it.
//
// Shape under STORAGE_KEY_TUTORIAL_STATE:
//   { [familyId]: { opened?: ms, completed?: ms, dismissed?: ms } }

/** Fired on document whenever a registry field is written. */
export const TUTORIAL_STATE_EVENT = 'forge:tutorial-state-change';

/**
 * Fired on document when a tour appears or leaves. `detail.running` is true
 * while the overlay is on screen. Consumed by the mobile drawer, which must
 * not claim `aria-modal` over a tour card sitting outside it.
 */
export const TUTORIAL_RUN_EVENT = 'forge:tutorial-run-change';

// A mode variant is the same tutorial wearing the current interface — one
// welcome card, one family record ('classic-intro' counts as 'intro').
const TUTORIAL_FAMILY_OF = (() => {
  const map = {};
  for (const [familyId, tutorial] of Object.entries(TUTORIALS)) {
    for (const variantId of Object.values(tutorial.modeVariants || {})) {
      map[variantId] = familyId;
    }
  }
  return map;
})();

/**
 * Resolve a tutorial id to its family id.
 * @param {string} tutorialId - Requested or resolved tutorial id
 * @returns {string} The family id ('classic-intro' -> 'intro')
 */
export function getTutorialFamilyId(tutorialId) {
  return TUTORIAL_FAMILY_OF[tutorialId] || tutorialId;
}

function readTutorialRegistry() {
  const raw = safeGetItem(STORAGE_KEY_TUTORIAL_STATE);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch (e) {
    console.warn('[Tutorial] Ignoring corrupt tutorial-state value:', e);
  }
  return {};
}

function writeTutorialRegistryField(tutorialId, field) {
  const familyId = getTutorialFamilyId(tutorialId);
  const registry = readTutorialRegistry();
  const existing = registry[familyId];
  const entry =
    existing && typeof existing === 'object' && !Array.isArray(existing)
      ? { ...existing }
      : {};
  entry[field] = Date.now();
  registry[familyId] = entry;
  safeSetItem(STORAGE_KEY_TUTORIAL_STATE, JSON.stringify(registry));
  document.dispatchEvent(
    new CustomEvent(TUTORIAL_STATE_EVENT, { detail: { familyId, field } })
  );
}

/**
 * Read a family's persistent record.
 * @param {string} tutorialId - Any id in the family
 * @returns {{opened?: number, completed?: number, dismissed?: number}}
 */
export function getTutorialFamilyState(tutorialId) {
  const entry = readTutorialRegistry()[getTutorialFamilyId(tutorialId)];
  return entry && typeof entry === 'object' && !Array.isArray(entry)
    ? entry
    : {};
}

/** @param {string} tutorialId - The resolved id of the tutorial that opened */
export function recordTutorialOpened(tutorialId) {
  writeTutorialRegistryField(tutorialId, 'opened');
}

/** @param {string} tutorialId - The resolved id of the completed tutorial */
export function recordTutorialCompleted(tutorialId) {
  writeTutorialRegistryField(tutorialId, 'completed');
}

/**
 * @param {string} tutorialId - The family whose spotlight was dismissed
 *   for good
 */
export function recordTutorialSpotlightDismissed(tutorialId) {
  writeTutorialRegistryField(tutorialId, 'dismissed');
}

/**
 * Keep the promise `aria-modal` makes. Each of these dialogs is two buttons,
 * so the trap is a Tab cycle over them plus an Escape answer.
 *
 * One implementation for the resume, error and mode-choice dialogs, so
 * none of them claims aria-modal without a trap.
 *
 * @param {HTMLElement} modal - The dialog element
 * @param {() => void} onEscape - What Escape answers
 * @returns {(e: KeyboardEvent) => void} The handler, so callers can remove it
 */
function trapTwoButtonDialog(modal, onEscape) {
  const onKeydown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // The tutorial's own document-level Escape handler must not also fire
      e.stopPropagation();
      onEscape();
      return;
    }
    if (e.key !== 'Tab') return;

    const buttons = modal.querySelectorAll('button[data-action]');
    if (buttons.length === 0) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  modal.addEventListener('keydown', onKeydown);
  return onKeydown;
}

/**
 * Show a resume dialog when progress exists
 * @param {number} stepIndex - Saved step index
 * @param {number} totalSteps - Total steps in tutorial
 * @returns {Promise<boolean>} True to resume, false to start over
 */
function showTutorialResumeDialog(stepIndex, totalSteps) {
  return new Promise((resolve) => {
    const modal = document.createElement('div');
    modal.className = 'preset-modal confirm-modal tutorial-resume-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'tutorialResumeTitle');
    modal.setAttribute('aria-describedby', 'tutorialResumeMessage');
    modal.dataset.testid = 'tutorial-resume-dialog';
    modal.style.setProperty('z-index', 'var(--z-index-tutorial-dialog)');

    modal.innerHTML = `
      <div class="preset-modal-content confirm-modal-content">
        <div class="preset-modal-header">
          <h3 id="tutorialResumeTitle" class="preset-modal-title">Resume tutorial?</h3>
        </div>
        <div class="confirm-modal-body">
          <p id="tutorialResumeMessage">You were on step ${stepIndex + 1} of ${totalSteps}. Would you like to resume?</p>
        </div>
        <div class="preset-form-actions">
          <button type="button" class="btn btn-primary" data-action="resume">Resume</button>
          <button type="button" class="btn btn-secondary" data-action="restart">Start over</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const cleanup = (shouldResume) => {
      modal.removeEventListener('keydown', onKeydown);
      modal.remove();
      resolve(shouldResume);
    };

    // Escape answers the same way clicking outside already does: start over
    const onKeydown = trapTwoButtonDialog(modal, () => cleanup(false));

    modal.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-action]');
      if (!btn) return;
      cleanup(btn.dataset.action === 'resume');
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        cleanup(false);
      }
    });

    const resumeBtn = modal.querySelector('button[data-action="resume"]');
    resumeBtn?.focus();
  });
}

/**
 * Show an error recovery dialog after repeated failures
 * @param {string} message - Error message
 * @returns {Promise<boolean>} True to restart, false to exit
 */
function showTutorialErrorDialog(message) {
  return new Promise((resolve) => {
    const modal = document.createElement('div');
    modal.className = 'preset-modal confirm-modal tutorial-error-modal';
    modal.setAttribute('role', 'alertdialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'tutorialErrorTitle');
    modal.setAttribute('aria-describedby', 'tutorialErrorMessage');
    modal.dataset.testid = 'tutorial-error-dialog';
    modal.style.setProperty('z-index', 'var(--z-index-tutorial-dialog)');

    modal.innerHTML = `
      <div class="preset-modal-content confirm-modal-content">
        <div class="preset-modal-header">
          <h3 id="tutorialErrorTitle" class="preset-modal-title">Tutorial needs help</h3>
        </div>
        <div class="confirm-modal-body">
          <p id="tutorialErrorMessage">${message}</p>
        </div>
        <div class="preset-form-actions">
          <button type="button" class="btn btn-primary" data-action="restart">Restart</button>
          <button type="button" class="btn btn-secondary" data-action="exit">Exit</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const cleanup = (shouldRestart) => {
      modal.removeEventListener('keydown', onKeydown);
      modal.remove();
      resolve(shouldRestart);
    };

    // Escape answers the same way clicking outside already does: exit
    const onKeydown = trapTwoButtonDialog(modal, () => cleanup(false));

    modal.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-action]');
      if (!btn) return;
      cleanup(btn.dataset.action === 'restart');
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        cleanup(false);
      }
    });

    const restartBtn = modal.querySelector('button[data-action="restart"]');
    restartBtn?.focus();
  });
}

/**
 * Ask before a tutorial switches the interface, with the interface names
 * filled in per direction. Resolves true to switch.
 * @param {Object} tutorial - Entry from TUTORIALS
 * @returns {Promise<boolean>}
 */
function showTutorialModeChoiceDialog(tutorial) {
  return new Promise((resolve) => {
    const classicHome = (tutorial.homeModes || FORGE_HOME_MODES).includes(
      'classic'
    );
    const homeName = classicHome ? 'Classic' : 'Assistive Forge';
    const stayName = classicHome ? 'Assistive Forge' : 'Classic';

    const modal = document.createElement('div');
    modal.className = 'preset-modal confirm-modal tutorial-mode-choice-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'tutorialModeChoiceTitle');
    modal.setAttribute('aria-describedby', 'tutorialModeChoiceMessage');
    modal.dataset.testid = 'tutorial-mode-choice-dialog';
    modal.style.setProperty('z-index', 'var(--z-index-tutorial-dialog)');

    modal.innerHTML = `
      <div class="preset-modal-content confirm-modal-content">
        <div class="preset-modal-header">
          <h3 id="tutorialModeChoiceTitle" class="preset-modal-title">This tour runs in ${homeName}</h3>
        </div>
        <div class="confirm-modal-body">
          <p id="tutorialModeChoiceMessage">The ${tutorial.title} tour is designed for the ${homeName} interface. To follow it, switch to ${homeName}. Your loaded file stays open.</p>
        </div>
        <div class="preset-form-actions">
          <button type="button" class="btn btn-primary" data-action="switch">Switch and start the tour</button>
          <button type="button" class="btn btn-secondary" data-action="stay">Stay in ${stayName}</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    const cleanup = (shouldSwitch) => {
      modal.removeEventListener('keydown', onKeydown);
      modal.remove();
      resolve(shouldSwitch);
    };

    // aria-modal promises focus stays inside: cycle the two buttons on Tab
    const onKeydown = trapTwoButtonDialog(modal, () => cleanup(false));

    modal.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-action]');
      if (!btn) return;
      cleanup(btn.dataset.action === 'switch');
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        cleanup(false);
      }
    });

    const switchBtn = modal.querySelector('button[data-action="switch"]');
    switchBtn?.focus();
  });
}

// ============================================================================
// Body Scroll Locking
// ============================================================================

/**
 * Lock body scrolling while tutorial is active
 */
function lockBodyScroll() {
  // The mobile Customizer drawer already locks body scroll and sets body.top.
  // Locking again here can create weird fixed-position offsets on mobile.
  if (document.body.classList.contains('drawer-open')) {
    didLockBodyScroll = false;
    return;
  }
  // A welcome-surface tour spans the header and the welcome column.
  // Reaching its start card means the document may be scrolled, and the
  // body lock freezes that offset into body.top — pinning the header
  // permanently off-view, where every header step fails.
  // Zero the document first; the welcome column keeps its own scroll for
  // the per-step reveals lower down.
  if (activeTutorial?.surface === 'welcome' && window.scrollY > 0) {
    window.scrollTo(0, 0);
  }
  scrollYBeforeLock = window.scrollY;
  document.body.classList.add('tutorial-body-locked');
  document.body.style.top = `-${scrollYBeforeLock}px`;
  didLockBodyScroll = true;
}

/**
 * Unlock body scrolling when tutorial closes
 */
function unlockBodyScroll() {
  if (!didLockBodyScroll) return;
  document.body.classList.remove('tutorial-body-locked');
  document.body.style.top = '';
  window.scrollTo(0, scrollYBeforeLock);
  didLockBodyScroll = false;
}

// ============================================================================
// Navigation Debouncing
// ============================================================================

/**
 * Navigate to a step with debouncing to prevent rapid clicks
 * @param {number} newIndex - Target step index
 */
async function navigateToStep(newIndex) {
  if (isNavigating) {
    console.log('Navigation in progress, ignoring click');
    return;
  }

  isNavigating = true;
  // Prevent "double taps" from being ignored silently on mobile.
  // While we are navigating, mark the nav buttons busy so the UI reflects
  // it; busy, not disabled, so the button pressed keeps focus (a disabled
  // one dropped it to the page body on every step). isNavigating ignores a
  // second press.
  const backBtn = tutorialOverlay?.querySelector('#tutorialBackBtn');
  const nextBtn = tutorialOverlay?.querySelector('#tutorialNextBtn');
  setBusy(backBtn, true);
  setBusy(nextBtn, true);
  try {
    await showStep(newIndex);
    consecutiveFailures = 0;
    // The new step's heading takes focus, so its title, its place in the
    // tour and then its text are read in order. Left on Next, a screen
    // reader heard only a title, never the step's text.
    const heading = tutorialOverlay?.querySelector('#tutorial-step-title');
    if (heading && (heading.checkVisibility?.() ?? true)) heading.focus();
  } finally {
    isNavigating = false;
    // Re-enable buttons based on the current step state
    if (tutorialOverlay) {
      const currentStep = activeTutorial?.steps?.[currentStepIndex];
      const back = tutorialOverlay.querySelector('#tutorialBackBtn');
      const next = tutorialOverlay.querySelector('#tutorialNextBtn');
      setBusy(back, false);
      setBusy(next, false);
      const backOff = currentStepIndex === 0;
      const nextOff = !!currentStep?.completion && !stepCompleted;
      next?.setAttribute('aria-disabled', String(nextOff));
      // Back on the first step and Next on a step still waiting turn off:
      // focus goes to the other one, not to the page body.
      if (back && !backOff) back.disabled = false;
      if (next && !nextOff) next.disabled = false;
      if (back && backOff) disableKeepingFocus(back, nextOff ? null : next);
      if (next && nextOff) disableKeepingFocus(next, backOff ? null : back);
    }
  }
}

/**
 * Find the next step index that matches showWhen conditions
 * @param {number} startIndex - Index to start searching from
 * @param {number} direction - 1 for forward, -1 for backward
 * @returns {number|null}
 */
function findNextValidStepIndex(startIndex, direction) {
  const steps = activeTutorial?.steps || [];
  for (let i = startIndex; i >= 0 && i < steps.length; i += direction) {
    const step = steps[i];
    if (!step?.showWhen || evaluateShowWhenCondition(step.showWhen)) {
      return i;
    }
  }
  return null;
}

/**
 * Skip to the next valid step with an announcement
 * @param {number} stepIndex - Current step index
 * @param {number} direction - Direction to move
 * @param {string} reason - Skip reason
 */
async function skipToNextValidStep(
  stepIndex,
  direction,
  reason,
  { completedIfPastEnd = true } = {}
) {
  announceToScreenReader(`Step skipped: ${reason}`);
  const nextIndex = findNextValidStepIndex(stepIndex + direction, direction);
  if (nextIndex === null) {
    // Skipping past the end because the remaining steps do not
    // apply to this layout is a finish; skipping past it because a step
    // failed taught nothing, so it must not be recorded as one. The caller
    // says which it is.
    closeTutorial(completedIfPastEnd);
    return;
  }
  await showStep(nextIndex);
}

/**
 * Handle repeated failures and offer recovery options
 * @param {string} reason - Failure reason
 * @param {number} stepIndex - Step index where failure occurred
 * @param {number} direction - Direction to move on skip
 */
async function handleStepFailure(reason, stepIndex, direction) {
  consecutiveFailures += 1;

  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
    announceToScreenReader(
      'Tutorial encountered an issue. Choose to restart or exit.',
      'assertive'
    );
    const shouldRestart = await showTutorialErrorDialog(
      'Tutorial is having trouble continuing. Would you like to restart or exit?'
    );

    consecutiveFailures = 0;
    if (shouldRestart) {
      clearTutorialProgress();
      await showStep(0);
    } else {
      closeTutorial();
    }
    return;
  }

  await skipToNextValidStep(stepIndex, direction, reason, {
    completedIfPastEnd: false,
  });
}

// ============================================================================
// Target Removal Detection
// ============================================================================

/**
 * Watch for the current target element being removed from DOM
 * @param {HTMLElement} targetElement - Element to watch
 * @param {Function} onRemoved - Callback when element is removed
 * @returns {MutationObserver}
 */
function watchTargetRemoval(targetElement, onRemoved) {
  if (targetRemovalObserver) {
    targetRemovalObserver.disconnect();
  }

  targetRemovalObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.removedNodes) {
        if (
          node === targetElement ||
          (node.contains && node.contains(targetElement))
        ) {
          targetRemovalObserver.disconnect();
          targetRemovalObserver = null;
          onRemoved();
          return;
        }
      }
    }
  });

  targetRemovalObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return targetRemovalObserver;
}

/**
 * Watch the step's target and survive a re-render.
 *
 * Anchor resilience: a mode or density change rebuilds panels, which
 * removes the highlighted node and puts an equivalent one back a moment
 * later. Look for that replacement once before treating the removal as a
 * failure, so an interface change costs the user nothing.
 *
 * @param {HTMLElement} target - Element to watch
 * @param {number} stepIndex - The step this watch belongs to
 */
function armTargetRemovalWatch(target, stepIndex) {
  watchTargetRemoval(target, () => {
    if (!activeTutorial || currentStepIndex !== stepIndex) return;
    void (async () => {
      const step = activeTutorial?.steps?.[stepIndex];
      const replacement = step ? await resolveTargetWithRetry(step, 600) : null;
      if (!activeTutorial || currentStepIndex !== stepIndex) return;

      if (replacement) {
        currentTarget = replacement;
        armTargetRemovalWatch(replacement, stepIndex);
        updateSpotlightAndPosition();
        return;
      }

      announceToScreenReader(
        'Tutorial step interrupted: element no longer available.'
      );
      currentTarget = null;
      void handleStepFailure('target removed', stepIndex, 1);
    })();
  });
}

/**
 * Re-settle the current step after the interface changed under it.
 *
 * A layout change the tour can live with
 * must not end it. If the step's subject left the screen it skips forward
 * with the reason it already carries; otherwise the spotlight simply follows
 * the element to wherever it now is. The completion gate is deliberately not
 * rebuilt, so a step whose action the user has already done does not ask for
 * it a second time.
 *
 * @param {string} reason - Skip reason to speak if the step no longer applies
 */
async function reResolveStepAfterLayoutChange(reason) {
  if (!activeTutorial || !tutorialOverlay) return;
  const stepIndex = currentStepIndex;
  const step = activeTutorial.steps[stepIndex];
  if (!step) return;

  if (step.showWhen && !evaluateShowWhenCondition(step.showWhen)) {
    await skipToNextValidStep(stepIndex, 1, step.skipReason || reason);
    return;
  }

  if (!step.highlightSelector && !step.targetKey) {
    updateSpotlightAndPosition();
    return;
  }

  const target = await resolveTargetWithRetry(step);
  if (!activeTutorial || currentStepIndex !== stepIndex) return;
  if (!target) {
    await handleStepFailure('target unavailable', stepIndex, 1);
    return;
  }

  currentTarget = target;
  armTargetRemovalWatch(target, stepIndex);
  updateSpotlightAndPosition();
}

// ============================================================================
// Pause/Resume for Visibility Changes
// ============================================================================

/**
 * Handle visibility change (tab switch, minimize)
 */
function handleVisibilityChange() {
  if (document.hidden && activeTutorial) {
    pauseTutorial();
  } else if (!document.hidden && isPaused) {
    resumeTutorial();
  }
}

/**
 * Pause the tutorial when tab is not visible
 */
function pauseTutorial() {
  if (!activeTutorial) return;
  isPaused = true;
  saveTutorialProgress(currentStepIndex);
  tutorialOverlay?.classList.add('tutorial-paused');
}

/**
 * Resume the tutorial when tab becomes visible
 */
function resumeTutorial() {
  if (!activeTutorial || !isPaused) return;
  isPaused = false;
  tutorialOverlay?.classList.remove('tutorial-paused');
  // Re-measure and reposition in case layout changed
  scheduleReposition();
}

/**
 * Handle beforeunload to save progress
 */
function handleBeforeUnload() {
  if (activeTutorial && currentStepIndex > 0) {
    saveTutorialProgress(currentStepIndex);
  }
}

/**
 * Handle browser back/forward navigation during tutorial
 *
 * Progress is saved first, so a tour that ends here can be resumed. When
 * the app itself answered the Back press (its dialog, where the person can
 * choose "Stay in the app"), the tour stays on the step it was on; any
 * other Back press ends it.
 */
function handlePopState() {
  if (!activeTutorial) return;
  saveTutorialProgress(currentStepIndex);
  if (backGuardAnsweredPop()) return;
  closeTutorial();
}

// ============================================================================
// Touch Event Handling
// ============================================================================

/**
 * Setup touch handlers for tutorial panel
 * @param {HTMLElement} panel - Tutorial panel element
 */
function setupTouchHandlers(panel) {
  let touchStartTime = 0;
  let lastTapTime = 0;

  // Prevent double-tap zoom on tutorial buttons
  const handleTouchStart = (e) => {
    if (
      e.target.matches(
        '.tutorial-btn-back, .tutorial-btn-next, .tutorial-close, .tutorial-minimize'
      )
    ) {
      touchStartTime = Date.now();

      // Prevent double-tap zoom
      const timeSinceLastTap = touchStartTime - lastTapTime;
      if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = (e) => {
    if (
      e.target.matches(
        '.tutorial-btn-back, .tutorial-btn-next, .tutorial-close, .tutorial-minimize'
      )
    ) {
      const touchDuration = Date.now() - touchStartTime;
      lastTapTime = Date.now();

      // Fast tap - trigger click immediately (bypasses 300ms delay)
      if (touchDuration < 200) {
        e.preventDefault();
        e.target.click();
      }
    }
  };

  // Prevent context menu on long press
  const handleContextMenu = (e) => {
    if (e.target.matches('.tutorial-btn-back, .tutorial-btn-next')) {
      e.preventDefault();
    }
  };

  panel.addEventListener('touchstart', handleTouchStart, { passive: false });
  panel.addEventListener('touchend', handleTouchEnd, { passive: false });
  panel.addEventListener('contextmenu', handleContextMenu);

  // Return cleanup function
  return () => {
    panel.removeEventListener('touchstart', handleTouchStart);
    panel.removeEventListener('touchend', handleTouchEnd);
    panel.removeEventListener('contextmenu', handleContextMenu);
  };
}

// ============================================================================
// Focus Trap (uses shared utility from focus-trap.js)
// ============================================================================

/**
 * Setup focus trap inside tutorial panel to keep focus within the dialog
 * Uses shared focus-trap.js utility for consistent behavior across components.
 * @param {HTMLElement} panelElement - The panel element to trap focus within
 * @returns {Function} Cleanup function to remove the trap
 */
function setupFocusTrap(panelElement) {
  const trap = createFocusTrap(panelElement);
  trap.activate();
  return () => trap.deactivate();
}

/**
 * Make background content inert while tutorial is active
 * @param {boolean} makeInert - Whether to make content inert
 * @note Reserved for future accessibility enhancement
 */
// eslint-disable-next-line no-unused-vars
function setBackgroundInert(makeInert) {
  // Find main app content containers (excluding tutorial overlay)
  const mainContent =
    document.getElementById('main-content') || document.querySelector('main');
  const appContainer = document.getElementById('app') || document.body;

  // Use inert attribute if supported, otherwise use aria-hidden
  const targets = [mainContent, appContainer].filter(
    (el) => el && !el.contains(tutorialOverlay)
  );

  targets.forEach((el) => {
    if (makeInert) {
      if ('inert' in el) {
        el.inert = true;
      } else {
        el.setAttribute('aria-hidden', 'true');
      }
    } else {
      if ('inert' in el) {
        el.inert = false;
      } else {
        el.removeAttribute('aria-hidden');
      }
    }
  });
}

/**
 * Start a tutorial
 * @param {string} tutorialId - Tutorial identifier
 * @param {Object} options - Options
 * @param {HTMLElement} [options.triggerEl] - Element that triggered the tutorial
 */
export async function startTutorial(tutorialId, { triggerEl } = {}) {
  let tutorial = TUTORIALS[tutorialId];
  if (!tutorial) {
    console.warn(`Tutorial "${tutorialId}" not found`);
    return;
  }

  // A tutorial may name a sibling built for
  // the current interface - the welcome card's intro launches the Classic
  // tour inside Classic, directly and in place. Resolve before any gating.
  const modeCtrl = getUIModeController();
  const variantId = tutorial.modeVariants?.[modeCtrl.getMode()];
  if (variantId && TUTORIALS[variantId]) {
    tutorialId = variantId;
    tutorial = TUTORIALS[variantId];
  }

  // Tutorials declare the interface they are built for (homeModes; Forge
  // chrome by default). A cross-interface launch asks first; cancel stays
  // put.
  const homeModes = tutorial.homeModes || FORGE_HOME_MODES;
  if (!homeModes.includes(modeCtrl.getMode())) {
    const proceed = await showTutorialModeChoiceDialog(tutorial);
    if (!proceed) {
      // The welcome card that triggered us may be gone (loading the example
      // dismissed the welcome screen), so fall back like closeTutorial does.
      if (triggerEl && document.contains(triggerEl) && triggerEl.offsetParent) {
        triggerEl.focus();
      } else {
        document.getElementById('main-content')?.focus?.();
      }
      return;
    }
  }

  let startIndex = 0;
  const savedProgress = loadTutorialProgress();
  if (
    savedProgress &&
    savedProgress.tutorialId === tutorialId &&
    Number.isInteger(savedProgress.stepIndex) &&
    savedProgress.stepIndex > 0 &&
    savedProgress.stepIndex < tutorial.steps.length
  ) {
    const shouldResume = await showTutorialResumeDialog(
      savedProgress.stepIndex,
      tutorial.steps.length
    );
    if (shouldResume) {
      startIndex = savedProgress.stepIndex;
    } else {
      clearTutorialProgress();
    }
  }

  // A tour already on screen has to be closed before this run captures any
  // state: closeTutorial resets the module's globals, so closing it later
  // would wipe the incoming tutorial and leave no tour at all. Every abort
  // path above has already returned, so nothing is destroyed for a tour
  // that then fails to open.
  if (tutorialOverlay) {
    closeTutorial(false, { skipAnnouncement: true });
  }

  // Store current focus to restore on close
  previousFocus = document.activeElement;
  triggerElement = triggerEl || previousFocus;
  activeTutorial = tutorial;
  currentStepIndex = startIndex;
  isMinimized = false;

  // Move to the tutorial's home interface (consented above) or its declared
  // forceMode (the intro tour runs in Simplified). Capture the pre-tutorial
  // state first: the forced switch must never leave its mode behind as the
  // user's density or saved preference.
  preTutorialMode = modeCtrl.getMode();
  preTutorialDensity = modeCtrl.getClassicDensity();
  tutorialForcedMode = false;
  const forcedTarget = !homeModes.includes(preTutorialMode)
    ? tutorial.forceMode ||
      (homeModes.includes(preTutorialDensity)
        ? preTutorialDensity
        : homeModes[0])
    : tutorial.forceMode && tutorial.forceMode !== preTutorialMode
      ? tutorial.forceMode
      : null;
  if (forcedTarget) {
    tutorialForcedMode =
      modeCtrl.switchMode(forcedTarget, {
        skipAnnouncement: true,
        skipFocus: true,
      }) === true;
    if (!tutorialForcedMode && !homeModes.includes(modeCtrl.getMode())) {
      // The consented switch was refused (the viewport gate): never
      // start a tour whose interface is absent.
      announceToScreenReader(
        'Classic needs a wider window right now, so the tour cannot start. Your file stays open.',
        'assertive'
      );
      preTutorialMode = null;
      preTutorialDensity = null;
      return;
    }
  }

  // The tutorial is now committed to open: every abort path (missing id,
  // declined consent, refused viewport switch) has already returned.
  recordTutorialOpened(tutorialId);

  createTutorialOverlay();

  // A user interface switch is handled here
  modeChangeUnsubscribe = modeCtrl.subscribe((newMode) =>
    handleModeChangeDuringTutorial(newMode)
  );
  // The Classic density switch never reaches those subscribers
  // (setClassicDensity dispatches this event instead), so the tour listens
  // for it directly and re-settles the same way.
  densityChangeListener = () => {
    void reResolveStepAfterLayoutChange('the Classic view changed');
  };
  document.addEventListener('classic-density-change', densityChangeListener);
  // Spotlight cutouts stay clickable, so a user can open a project in the
  // middle of a welcome-surface tour. That action wins the same way a mode
  // switch does; the tour closes with its surface. It is symmetrical: a
  // project tour ends on a step that invites a press of the Main Page
  // button, and its steps point at chrome the welcome screen does not have.
  watchSurfaceChangeDuringTutorial(
    tutorial.surface === 'welcome' ? 'welcome' : 'project'
  );
  watchDialogsDuringTutorial();
  await showStep(startIndex);
  announceToScreenReader(
    `${tutorial.title} started. Step ${startIndex + 1} of ${tutorial.steps.length}. Press Escape to exit at any time.`,
    'assertive'
  );
}

/**
 * Create the tutorial overlay DOM structure
 */
function createTutorialOverlay() {
  // startTutorial closes a running tour before it captures state, so
  // this is only a stale-node guard and must not reset the module's state:
  // closeTutorial would null the activeTutorial this overlay is being built
  // for.
  if (tutorialOverlay) {
    tutorialOverlay.remove();
    tutorialOverlay = null;
  }

  const overlay = document.createElement('div');
  overlay.className = 'tutorial-overlay';
  // Role and aria-label on outer container for screen reader context
  overlay.setAttribute('role', 'presentation');
  overlay.setAttribute('aria-hidden', 'false');

  overlay.innerHTML = `
    <svg class="tutorial-spotlight-svg" aria-hidden="true">
      <defs>
        <mask id="tutorial-spotlight-mask">
          <rect x="0" y="0" width="100%" height="100%" fill="white"/>
          <rect class="tutorial-spotlight-cutout" x="0" y="0" width="0" height="0" rx="${SPOTLIGHT_RADIUS}" fill="black"/>
        </mask>
      </defs>
      <rect class="tutorial-backdrop" x="0" y="0" width="100%" height="100%" mask="url(#tutorial-spotlight-mask)"/>
    </svg>
    
    <div class="tutorial-panel" 
         role="dialog" 
         aria-modal="true"
         aria-labelledby="tutorial-panel-title" 
         aria-describedby="tutorial-panel-content"
         tabindex="-1">
      <div class="tutorial-arrow" aria-hidden="true"></div>
      
      <div class="tutorial-header">
        <h2 id="tutorial-panel-title" class="tutorial-title">${activeTutorial.title}</h2>
        <div class="tutorial-header-actions">
          <button class="tutorial-minimize" aria-label="Minimize tutorial" title="Minimize" type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
          <button class="tutorial-close" aria-label="Exit tutorial (Escape)" title="Exit tutorial (Escape)" type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>
      
      <div class="tutorial-body">
        <div id="tutorial-panel-content" class="tutorial-content"></div>
        <div class="tutorial-requirement" id="tutorialRequirement" role="status" aria-live="polite"></div>
      </div>

      <div class="tutorial-scroll-cue" aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      <div class="tutorial-footer">
        <div class="tutorial-progress" id="tutorialProgress">
          Step <span id="tutorial-step-current">1</span> of <span id="tutorial-step-total">${shownStepCount()}</span>
        </div>
        <div class="tutorial-nav">
          <button class="btn btn-sm tutorial-btn-back" id="tutorialBackBtn" type="button" disabled aria-label="Previous step">← Back</button>
          <button class="btn btn-sm tutorial-btn-next" id="tutorialNextBtn" type="button" aria-label="Next step">Next →</button>
        </div>
        <details class="tutorial-keyboard-help" id="tutorialKeyboardHelp">
          <summary aria-label="Keyboard shortcuts" title="Keyboard shortcuts">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <rect x="2" y="6" width="20" height="12" rx="2"></rect>
              <path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M6 14h12"></path>
            </svg>
          </summary>
          <div class="tutorial-keyboard-help-content" role="note">
            <div><kbd>←</kbd>/<kbd>→</kbd> or <kbd>b</kbd>/<kbd>n</kbd>: steps</div>
            <div><kbd>Esc</kbd>: exit</div>
            <div><kbd>Home</kbd>/<kbd>End</kbd>: first/last</div>
            <div><kbd>?</kbd>: open this help</div>
          </div>
        </details>
      </div>
    </div>
    
    <div class="tutorial-minimized hidden">
      <button class="tutorial-restore" aria-label="Restore tutorial" type="button">
        <span class="tutorial-minimized-text">Tutorial</span>
        <span class="tutorial-minimized-progress">${currentStepIndex + 1}/${activeTutorial.steps.length}</span>
      </button>
    </div>
  `;

  document.body.appendChild(overlay);
  tutorialOverlay = overlay;

  // Lock body scroll
  lockBodyScroll();

  setupTutorialListeners();
  setupResizeObserver();

  // Setup focus trap and touch handlers on the panel
  const panel = tutorialOverlay.querySelector('.tutorial-panel');
  if (panel) {
    focusTrapCleanup = setupFocusTrap(panel);
    setupTouchHandlers(panel);

    // Focus starts on Next: the first button was Minimize, and Enter there
    // put the tour away. At once, so focus does not sit on the page body
    // while the button that started the tour goes; again on the next frame
    // if the panel was not ready to take it.
    const firstFocusable =
      panel.querySelector('#tutorialNextBtn:not(:disabled)') ||
      panel.querySelector('button:not(:disabled)') ||
      panel;
    firstFocusable.focus();
    requestAnimationFrame(() => {
      if (!panel.contains(document.activeElement)) firstFocusable.focus();
    });
  }

  // Setup visibility change listener for pause/resume
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Setup beforeunload to save progress
  window.addEventListener('beforeunload', handleBeforeUnload);

  // Handle browser navigation (back/forward) during tutorial
  window.addEventListener('popstate', handlePopState);

  announceTutorialRunState(true);
}

/**
 * Tell the rest of the app that a tour is or is not on screen.
 *
 * A document event rather than an import, for the reason the drawer's own
 * `ui-mode-changed` listener already gives: the drawer must not depend on the
 * tutorial module, and the tutorial must not reach into the drawer.
 *
 * @param {boolean} running
 */
function announceTutorialRunState(running) {
  document.dispatchEvent(
    new CustomEvent(TUTORIAL_RUN_EVENT, { detail: { running } })
  );
}

/**
 * Set up event listeners for tutorial controls
 */
function setupTutorialListeners() {
  const closeBtn = tutorialOverlay.querySelector('.tutorial-close');
  const minimizeBtn = tutorialOverlay.querySelector('.tutorial-minimize');
  const restoreBtn = tutorialOverlay.querySelector('.tutorial-restore');
  const backBtn = tutorialOverlay.querySelector('#tutorialBackBtn');
  const nextBtn = tutorialOverlay.querySelector('#tutorialNextBtn');

  // Not closeTutorial itself: addEventListener would hand the click event
  // to its `completed` parameter, so the X would record the tour as
  // finished and throw the saved progress away.
  closeBtn?.addEventListener('click', () => closeTutorial(false));
  minimizeBtn?.addEventListener('click', toggleMinimize);
  restoreBtn?.addEventListener('click', restoreFromBar);

  backBtn?.addEventListener('click', () =>
    navigateToStep(currentStepIndex - 1)
  );
  nextBtn?.addEventListener('click', handleNextClick);

  // The cue has to retire as the reader reaches the end, not just appear.
  tutorialOverlay
    .querySelector('.tutorial-body')
    ?.addEventListener('scroll', updateScrollCue, { passive: true });

  // Click outside panel to focus target (if there is one)
  tutorialOverlay.addEventListener('click', handleOverlayClick);

  document.addEventListener('keydown', noteDialogBeforeKeydown, true);
  document.addEventListener('keydown', handleKeydown);
}

/**
 * Record, before anyone has had a chance to close it, whether something
 * else owned this Escape. Capture phase, so this runs ahead of the
 * dialog's own handler.
 *
 * The open Customizer drawer counts here even though it does not count as
 * a dialog anywhere else: its focus trap answers Escape by closing itself
 * and does not stop the event, so without this the one press would close
 * the drawer and end the tour. One Escape, one surface; the next Escape
 * exits.
 */
function noteDialogBeforeKeydown(e) {
  if (e.key === 'Escape') {
    escapeOwnedElsewhere = isAppDialogOpen() || isMobileDrawerOpen();
  }
}

/**
 * Handle keyboard events
 */
function handleKeydown(e) {
  if (!tutorialOverlay) return;

  // Don't intercept keyboard shortcuts if user is typing in an input
  const activeElement = document.activeElement;
  const isTyping =
    activeElement &&
    (activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable);

  if (e.key === 'Escape') {
    // While a dialog the user opened from a spotlight is on screen,
    // Escape belongs to that dialog. Stand aside and let the app's own
    // handler close it; the next Escape exits the tour.
    //
    // The dialog's own handler usually runs first and removes the dialog, so
    // by the time this one is reached there is nothing left to see. That is
    // what noteDialogBeforeKeydown records, on the capture phase.
    if (escapeOwnedElsewhere || isAppDialogOpen() || isMobileDrawerOpen())
      return;
    e.preventDefault();
    closeTutorial();
  } else if (
    (e.key === 'ArrowRight' || e.key === 'n') &&
    !e.ctrlKey &&
    !e.metaKey &&
    !isTyping &&
    stepCompleted
  ) {
    e.preventDefault();
    if (currentStepIndex < activeTutorial.steps.length - 1) {
      navigateToStep(currentStepIndex + 1);
    }
  } else if (
    (e.key === 'ArrowLeft' || e.key === 'b') &&
    !e.ctrlKey &&
    !e.metaKey &&
    !isTyping
  ) {
    e.preventDefault();
    if (currentStepIndex > 0) {
      navigateToStep(currentStepIndex - 1);
    }
  } else if (e.key === 'Home' && !isTyping) {
    e.preventDefault();
    navigateToStep(0);
  } else if (e.key === 'End' && !isTyping) {
    e.preventDefault();
    navigateToStep(activeTutorial.steps.length - 1);
  } else if (e.key === '?' && !isTyping) {
    e.preventDefault();
    showKeyboardHelp();
  }
}

/**
 * Show keyboard shortcuts help dialog
 */
function showKeyboardHelp() {
  const details = tutorialOverlay?.querySelector('#tutorialKeyboardHelp');
  if (details instanceof HTMLDetailsElement) {
    details.open = true;
    const summary = details.querySelector('summary');
    summary?.focus?.();
  }
  announceToScreenReader(
    'Keyboard shortcuts help: Arrow keys or n and b to navigate steps, Home and End to jump to first or last step, Escape to exit, Question mark to show this help'
  );
}

/**
 * Handle clicks on the overlay (outside the panel)
 */
function handleOverlayClick(e) {
  if (!tutorialOverlay) {
    return;
  }
  const panel = tutorialOverlay.querySelector('.tutorial-panel');
  const minimized = tutorialOverlay.querySelector('.tutorial-minimized');

  // If click is outside panel and minimized button, focus the highlighted element
  if (!panel?.contains(e.target) && !minimized?.contains(e.target)) {
    const step = activeTutorial.steps[currentStepIndex];
    if (step.highlightSelector || step.targetKey) {
      const target = resolveStepTarget(step);
      if (target && typeof target.focus === 'function') {
        target.focus();
      }
    }
  }
}

/**
 * Handle Next button click
 */
function handleNextClick() {
  if (!stepCompleted) {
    announceToScreenReader('Complete the highlighted action first.');
    // Pulse the requirement text
    const reqEl = tutorialOverlay.querySelector('#tutorialRequirement');
    if (reqEl) {
      reqEl.classList.add('tutorial-requirement-pulse');
      setTimeout(
        () => reqEl.classList.remove('tutorial-requirement-pulse'),
        500
      );
    }
    return;
  }

  if (currentStepIndex < activeTutorial.steps.length - 1) {
    navigateToStep(currentStepIndex + 1);
  } else {
    // Tutorial completed
    announceToScreenReader(
      'Tutorial completed! You can now explore the application.',
      'assertive'
    );
    closeTutorial(true); // Mark as completed
  }
}

// Debounce timeout for repositioning
let repositionTimeout = null;

/**
 * Schedule a debounced reposition to prevent jank during rapid changes
 */
function scheduleReposition() {
  if (repositionTimeout) {
    cancelAnimationFrame(repositionTimeout);
  }
  repositionTimeout = requestAnimationFrame(() => {
    updateSpotlightAndPosition();
    // A dialog that resizes (orientation, on-screen keyboard) moves the gap
    // the pill was parked in.
    syncDialogStandDown();
    repositionTimeout = null;
  });
}

/**
 * Handle orientation change with delay for browser reflow
 */
function handleOrientationChange() {
  // Some mobile browsers report incorrect dimensions immediately after orientation change
  // Wait for browser reflow before repositioning
  setTimeout(() => {
    if (activeTutorial) {
      const step = activeTutorial.steps[currentStepIndex];
      if (step?.showWhen && !evaluateShowWhenCondition(step.showWhen)) {
        void skipToNextValidStep(currentStepIndex, 1, 'layout changed');
        return;
      }
    }
    scheduleReposition();
  }, 100);
}

/**
 * Setup resize observer to reposition panel on window changes
 * Includes visualViewport support for zoom/keyboard/browser chrome
 */
function setupResizeObserver() {
  // ResizeObserver for body size changes (debounced)
  resizeObserver = new ResizeObserver(() => {
    if (activeTutorial && tutorialOverlay) {
      scheduleReposition();
    }
  });
  resizeObserver.observe(document.body);

  // Scroll handler (debounced)
  window.addEventListener('scroll', scheduleReposition, { passive: true });

  // visualViewport API for zoom, keyboard, and browser chrome changes
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleReposition);
    window.visualViewport.addEventListener('scroll', scheduleReposition);
  }

  // Orientation change handler with delay
  window.addEventListener('orientationchange', handleOrientationChange);

  // screen.orientation API for more reliable detection
  if (screen.orientation) {
    screen.orientation.addEventListener('change', handleOrientationChange);
  }
}

/**
 * Toggle minimized state
 */
function toggleMinimize() {
  // User-initiated toggle
  setMinimized(!isMinimized, { auto: false });
}

/**
 * A plain restore. The open drawer does not count as a dialog, so nothing
 * re-minimizes the tour, and the tour comes back over the drawer the user
 * is working in instead of shutting it.
 */
function restoreFromBar() {
  toggleMinimize();
}

/**
 * Set minimized state (supports auto-minimize).
 * @param {boolean} minimized
 * @param {{auto?: boolean}} opts
 */
function setMinimized(minimized, { auto = false } = {}) {
  if (!tutorialOverlay) return;
  if (isMinimized === minimized) return;

  isMinimized = minimized;
  wasAutoMinimized = auto ? minimized : false;

  const panel = tutorialOverlay.querySelector('.tutorial-panel');
  const minimizedEl = tutorialOverlay.querySelector('.tutorial-minimized');
  const svg = tutorialOverlay.querySelector('.tutorial-spotlight-svg');

  if (isMinimized) {
    panel.classList.add('hidden');
    minimizedEl.classList.remove('hidden');
    svg.style.opacity = '0.3';
  } else {
    panel.classList.remove('hidden');
    minimizedEl.classList.add('hidden');
    svg.style.opacity = '1';
    updateSpotlightAndPosition();
    // A restore while a dialog is still up re-applies the very ring the
    // stand-down removed (showStep restores unconditionally). Stand it back
    // down in the same frame rather than letting it paint over the dialog.
    syncDialogStandDown();
  }

  announceToScreenReader(
    isMinimized ? 'Tutorial minimized' : 'Tutorial restored'
  );
}

function restoreIfAutoMinimized() {
  if (!tutorialOverlay) return;
  if (isMinimized && wasAutoMinimized) {
    setMinimized(false, { auto: true });
  }
}

/**
 * Every surface the app opens as a dialog. The tour's own panel carries
 * role="dialog" and its own choice dialogs are .preset-modal, so both are
 * excluded by isAppDialogOpen.
 */
const APP_DIALOG_SELECTOR =
  '.modal-overlay, .preset-modal, [role="dialog"], [role="alertdialog"], dialog[open]';
const TUTORIAL_OWN_DIALOGS =
  '.tutorial-resume-modal, .tutorial-error-modal, .tutorial-mode-choice-modal';

/**
 * The Customizer drawer wears role="dialog" for as long as it is open
 * (drawer-controller.js), because on a phone it covers the screen and holds
 * focus. Counted as a dialog, it would make the tour stand down on every
 * drawer step on a phone, and the drawer steps are most of the tour.
 *
 * The drawer is the app's own chrome and usually the very thing a step is
 * teaching, so it is not a dialog the tour has to get out of the way of. A
 * real dialog rendered inside the drawer still counts: only the panel
 * itself is skipped, never its contents.
 */
const APP_CHROME_DIALOG_SELECTOR = '#paramPanel';

const isRendered = (el) =>
  typeof el.checkVisibility === 'function'
    ? el.checkVisibility()
    : getComputedStyle(el).display !== 'none';

/**
 * Every dialog the user has open, outermost only. Document order puts an
 * ancestor before its own `.modal-overlay` child, so the first match of a
 * nested pair is the one worth measuring.
 * @returns {HTMLElement[]}
 */
function findOpenAppDialogs() {
  const open = [];
  for (const el of document.querySelectorAll(APP_DIALOG_SELECTOR)) {
    if (tutorialOverlay?.contains(el)) continue;
    if (el.closest(TUTORIAL_OWN_DIALOGS)) continue;
    if (el.matches(APP_CHROME_DIALOG_SELECTOR)) continue;
    if (!isRendered(el)) continue;
    if (open.some((outer) => outer.contains(el))) continue;
    open.push(el);
  }
  return open;
}

/**
 * Is a dialog the user opened currently on screen?
 * @returns {boolean}
 */
function isAppDialogOpen() {
  return findOpenAppDialogs().length > 0;
}

/** The box a dialog actually paints, not its full-viewport scrim. */
function dialogContentBox(dialog) {
  const content =
    dialog.querySelector('.preset-modal-content, .modal-content') || dialog;
  const rect = content.getBoundingClientRect();
  return rect.height > 0 && rect.width > 0 ? rect : null;
}

/**
 * A dialog opened from a spotlighted control has to be the topmost thing
 * on screen, and minimizing the tour alone does not make it one: the veil
 * would keep painting over the dialog at 0.3, the target would keep the
 * elevation and ring the highlight class gives it, and the pill would keep
 * its bottom-right corner. Without the stand-down, at 412x730
 * `document.elementFromPoint` at the Clear Cache dialog's Cancel and at its
 * red confirm both return `#clearStorageBtn`, and pressing that ringed
 * button again stacks a second dialog on the first.
 *
 * So while a user-opened dialog is up the tour stands down completely. The
 * one exception is a step whose own target lives inside that dialog: the
 * Features Guide steps, where the dialog is the subject and its deliberate
 * lit-through-the-cutout presentation is the step.
 */
function applyDialogStandDown(dialogs) {
  if (!tutorialOverlay) return;

  // The dialog is the thing the step is teaching: leave the spotlight alone.
  if (currentTarget && dialogs.some((d) => d.contains(currentTarget))) {
    releaseDialogStandDown();
    return;
  }

  const svg = tutorialOverlay.querySelector('.tutorial-spotlight-svg');
  const pill = tutorialOverlay.querySelector('.tutorial-minimized');

  if (!dialogStandDown) {
    dialogStandDown = {
      svgDisplay: svg ? svg.style.display : '',
      pillTop: pill ? pill.style.top : '',
      pillBottom: pill ? pill.style.bottom : '',
      pillDisplay: pill ? pill.style.display : '',
    };
  }

  // Every pass, not just the first: a restore re-applies the ring underneath
  // us, and showStep restores unconditionally.
  document.querySelectorAll('.tutorial-target-highlight').forEach((el) => {
    el.classList.remove('tutorial-target-highlight');
  });
  if (svg) svg.style.display = 'none';
  keepPillClearOfDialogs(pill, dialogs);
}

/**
 * Keep the pill off the dialog's own box - and otherwise leave it exactly where
 * it lives.
 *
 * A dialog the pill does not touch is a dialog it does not need to dodge:
 * at 1280x600 the keyboard-shortcuts dialog paints x 290-990 while the pill
 * sits at x 1152, so measuring only the vertical gaps would hide a pill
 * that could never have been covered.
 *
 * So: only act on a real intersection. Then prefer a gap above, then below, and
 * only when neither can hold it does the pill go - because covering a dialog's
 * controls is what this prevents, and a focusable control outside an
 * `aria-modal` dialog is a focus-trap leak besides. Closing the dialog restores
 * it, and the tour with it.
 */
function keepPillClearOfDialogs(pill, dialogs) {
  if (!pill) return;

  const boxes = dialogs.map(dialogContentBox).filter(Boolean);
  if (!boxes.length) return;

  // Always decide from the pill's resting corner, never from wherever an
  // earlier pass parked it.
  pill.style.top = dialogStandDown?.pillTop ?? '';
  pill.style.bottom = dialogStandDown?.pillBottom ?? '';
  pill.style.display = dialogStandDown?.pillDisplay ?? '';

  const resting = pill.getBoundingClientRect();
  const overlaps = (box) =>
    !(
      resting.right <= box.left ||
      resting.left >= box.right ||
      resting.bottom <= box.top ||
      resting.top >= box.bottom
    );
  if (!boxes.some(overlaps)) return;

  const pillHeight = resting.height || 44;
  const needed = pillHeight + 16;
  const above = Math.min(...boxes.map((b) => b.top));
  const below = window.innerHeight - Math.max(...boxes.map((b) => b.bottom));

  if (above >= needed) {
    pill.style.top = `${Math.max(8, Math.round((above - pillHeight) / 2))}px`;
    pill.style.bottom = 'auto';
    return;
  }

  if (below >= needed) {
    pill.style.top = 'auto';
    pill.style.bottom = `${Math.max(8, Math.round((below - pillHeight) / 2))}px`;
    return;
  }

  pill.style.display = 'none';
}

/**
 * Give back everything the stand-down took. The ring goes back on whatever the
 * step points at now, never on whoever happened to be wearing it when the
 * dialog opened: on the Features Guide steps the modal opens while the ring is
 * still on the Help button, and handing that button its ring back leaves a
 * blue orphan burning behind the modal.
 */
function releaseDialogStandDown() {
  if (!dialogStandDown) return;

  const { svgDisplay, pillTop, pillBottom, pillDisplay } = dialogStandDown;
  dialogStandDown = null;

  if (currentTarget?.isConnected) {
    currentTarget.classList.add('tutorial-target-highlight');
  }

  const svg = tutorialOverlay?.querySelector('.tutorial-spotlight-svg');
  if (svg) svg.style.display = svgDisplay;

  const pill = tutorialOverlay?.querySelector('.tutorial-minimized');
  if (pill) {
    pill.style.top = pillTop;
    pill.style.bottom = pillBottom;
    pill.style.display = pillDisplay;
  }
}

/**
 * Re-decide the stand-down against whatever is on screen right now. Called by
 * the dialog watcher, by every restore, and by the reposition scheduler, so a
 * dialog that resizes or a ring re-applied underneath us cannot leave the tour
 * painting over it.
 */
function syncDialogStandDown() {
  if (!activeTutorial || !tutorialOverlay) return;
  const dialogs = findOpenAppDialogs();
  if (dialogs.length) applyDialogStandDown(dialogs);
  else releaseDialogStandDown();
}

/**
 * Pressing a spotlighted control that opens a dialog must not leave the
 * dialog stranded under the veil: the Clear Cache dialog's z-index is 1000
 * against the veil's 10003, so it would open dimmed, with the tour panel
 * lying across it and focus on a button the user could barely see. While
 * any dialog is up the tour shrinks to its bar and the veil fades; when
 * the dialog goes, the tour comes back.
 */
function watchDialogsDuringTutorial() {
  let scheduled = false;

  const sync = () => {
    if (!activeTutorial || !tutorialOverlay) return;
    const dialogs = findOpenAppDialogs();
    const dialogUp = dialogs.length > 0;
    if (dialogUp && !isMinimized) {
      setMinimized(true, { auto: true });
    } else if (!dialogUp && isMinimized && wasAutoMinimized) {
      // Give the ring and the veil back before the panel returns, so the
      // restore recomputes from a whole spotlight rather than a stripped one.
      releaseDialogStandDown();
      setMinimized(false, { auto: true });
    }
    if (dialogUp) applyDialogStandDown(dialogs);
    else releaseDialogStandDown();
  };

  dialogObserver = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      sync();
    });
  });

  dialogObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'open', 'hidden', 'style'],
  });
}

/**
 * Show a specific tutorial step
 * @param {number} stepIndex - Step index
 */
async function showStep(stepIndex) {
  if (stepIndex < 0 || stepIndex >= activeTutorial.steps.length) return;

  // Clear previous state
  clearCompletionListeners();
  clearDrawerObserver();
  if (targetRemovalObserver) {
    targetRemovalObserver.disconnect();
    targetRemovalObserver = null;
  }
  currentTarget = null;

  const previousStepIndex = currentStepIndex;
  currentStepIndex = stepIndex;
  const direction = stepIndex >= previousStepIndex ? 1 : -1;
  const step = activeTutorial.steps[stepIndex];

  // Check if step should be shown based on showWhen conditions
  if (step.showWhen) {
    const shouldShow = evaluateShowWhenCondition(step.showWhen);
    if (!shouldShow) {
      const reason =
        step.skipReason ||
        (isMobileViewport()
          ? 'not applicable on mobile'
          : 'not applicable on desktop');
      await skipToNextValidStep(stepIndex, direction, reason);
      return;
    }
  }

  // Execute step preconditions (ensure actions) if defined
  if (step.ensure) {
    const preconditionsOk = await ensureStepPreconditions(step);
    if (!preconditionsOk) {
      await handleStepFailure('setup failed', stepIndex, direction);
      return;
    }
  }

  // Check if this step targets an element inside the param panel
  // If so, set up drawer observer and ensure drawer is open (both mobile and desktop)
  if (step.highlightSelector || step.targetKey) {
    // Set guard flag to prevent drawer observer from interfering during setup
    isSettingUpStep = true;

    // Watch the panel whenever the step has anything inside it to point at,
    // not only when the step opens it. A step can teach both halves (press
    // Customizer to open, press Close to shut) and arrive with the panel
    // shut, so without this the ring could not follow the user in.
    if (checkIfAnyTargetInsideDrawer(step)) {
      // Set up observer to detect drawer/panel state changes (mobile + desktop)
      setupDrawerObserver();
    }

    if (stepNeedsParamPanelOpen(step)) {
      // Open or expand the panel on both mobile and desktop: the tutorial
      // cannot proceed while the target is hidden in a collapsed panel.
      if (!isParamPanelOpen()) {
        openParamPanel();
        // Wait for animation to complete
        await waitForTransition(document.getElementById('paramPanel'), 400);
      }
    } else if (isMobileViewport() && isMobileDrawerOpen()) {
      // Nothing here needs the drawer, so close it on mobile: it covers the
      // screen, and whatever this step points at is behind it.
      closeMobileDrawer();
      await waitForTransition(document.getElementById('paramPanel'), 400);
    }
  }

  // Resolve target with retry (late DOM availability + visibility)
  let resolvedTarget = null;
  if (step.highlightSelector || step.targetKey) {
    // Welcome-surface targets live down one long scrolling column,
    // the resolver demands viewport intersection, and the engine's own
    // scroll runs only after resolution — so an off-screen target could
    // never resolve. Bring the candidate on screen first (instant, so the
    // rect is correct immediately). The project tours do the same inside the
    // Customizer, and nowhere else.
    if (activeTutorial.surface === 'welcome') {
      const candidate = resolveStepTarget(step, { requireVisible: false });
      if (candidate && !isElementVisible(candidate)) {
        scrollTargetIntoContainerView(candidate);
      }
    } else {
      bringIntoCustomizer(
        resolveStepTarget(step) ||
          resolveStepTarget(step, { requireVisible: false })
      );
    }
    resolvedTarget = await resolveTargetWithRetry(step);
    if (!resolvedTarget) {
      isSettingUpStep = false; // Clear flag before early return
      await handleStepFailure('target unavailable', stepIndex, direction);
      return;
    }
  }
  currentTarget = resolvedTarget;
  if (currentTarget) {
    armTargetRemovalWatch(currentTarget, stepIndex);
  }

  // Update content (use compact content if viewport is small/zoomed)
  const contentEl = tutorialOverlay.querySelector('#tutorial-panel-content');
  if (contentEl) {
    // The step's place in the tour is said with its title, from the counter
    // on screen; it was a live counter and an announcement of its own, said
    // twice per step.
    contentEl.innerHTML = `
      <h3 class="tutorial-step-title" id="tutorial-step-title" tabindex="-1" aria-describedby="tutorialProgress">${step.title}</h3>
      <div class="tutorial-step-content">${getStepContent(step)}</div>
    `;
  }

  // The body is a scroll container and it keeps its offset across an
  // innerHTML swap, so a reader who scrolled the previous step to the
  // bottom would arrive at the next one with its title already scrolled
  // off the top.
  const bodyEl = tutorialOverlay.querySelector('.tutorial-body');
  if (bodyEl) bodyEl.scrollTop = 0;

  // Update progress: the place among the steps this layout shows, so a
  // skipped step (phone-only, or hidden in Simplified) costs no number.
  const shownCurrent = shownStepNumber(stepIndex);
  const shownTotal = shownStepCount();
  const stepCurrentEl = tutorialOverlay.querySelector('#tutorial-step-current');
  if (stepCurrentEl) stepCurrentEl.textContent = shownCurrent;
  const stepTotalEl = tutorialOverlay.querySelector('#tutorial-step-total');
  if (stepTotalEl) stepTotalEl.textContent = shownTotal;
  const progressEl = tutorialOverlay.querySelector(
    '.tutorial-minimized-progress'
  );
  if (progressEl) progressEl.textContent = `${shownCurrent}/${shownTotal}`;
  const minimizedTextEl = tutorialOverlay.querySelector(
    '.tutorial-minimized-text'
  );
  if (minimizedTextEl)
    minimizedTextEl.textContent = step.autoMinimize
      ? 'Explore the modal'
      : 'Tutorial';

  // Update buttons
  const backBtn = tutorialOverlay.querySelector('#tutorialBackBtn');
  const nextBtn = tutorialOverlay.querySelector('#tutorialNextBtn');

  if (backBtn && stepIndex === 0) disableKeepingFocus(backBtn, nextBtn);
  else if (backBtn) backBtn.disabled = false;
  if (nextBtn) {
    const last = stepIndex === activeTutorial.steps.length - 1;
    nextBtn.textContent = last ? 'Finish ✓' : 'Next →';
    // The name follows the words on the button.
    nextBtn.setAttribute('aria-label', last ? 'Finish' : 'Next step');
  }

  // Setup completion gate
  setupCompletion(step);

  // Update spotlight and position
  updateSpotlightAndPosition();

  // Auto-minimize if requested (useful for modal overlays)
  if (step.autoMinimize && !isMinimized) {
    toggleMinimize();
  } else if (!step.autoMinimize && isMinimized) {
    // Restore if was minimized and current step doesn't need minimization
    toggleMinimize();
  }

  // Save progress to sessionStorage
  saveTutorialProgress(stepIndex);

  // Reset failure count on successful step display
  consecutiveFailures = 0;

  // Clear the guard flag - step setup is complete, drawer observer can now work
  isSettingUpStep = false;
}

/**
 * Park the card in the middle of the screen: the step asked for `center`, or
 * its target could not be resolved.
 *
 * One place for both callers, so neither leaves `dock()`'s inline `width`
 * and `maxHeight` behind or drops the class that keeps the mobile
 * bottom-sheet rule away; otherwise a centered card would be sized by one
 * step's dock arithmetic and padded by another rule's safe-area inset.
 * `tutorial-panel-centered` says which of the two un-docked states this is.
 *
 * @param {HTMLElement} panel
 * @param {HTMLElement} arrow
 */
function centerPanel(panel, arrow) {
  panel.style.position = 'fixed';
  panel.style.top = '50%';
  panel.style.left = '50%';
  panel.style.right = '';
  panel.style.bottom = '';
  panel.style.width = '';
  panel.style.maxHeight = '';
  panel.style.transform = 'translate(-50%, -50%)';
  panel.classList.remove('tutorial-panel-positioned');
  panel.classList.add('tutorial-panel-centered');
  if (arrow) arrow.style.display = 'none';
}

/**
 * Show that the card's body has more text below the fold.
 *
 * The body is a scroll container with two independent caps above it, and
 * without a cue it clips mid-sentence in silence. Presentation only: the
 * cue element is `aria-hidden`, takes no tab stop, and adds nothing to the
 * live regions; a screen-reader user reaches the rest through the scroll
 * container itself.
 */
function updateScrollCue() {
  if (!tutorialOverlay) return;
  const panel = tutorialOverlay.querySelector('.tutorial-panel');
  const body = tutorialOverlay.querySelector('.tutorial-body');
  if (!panel || !body) return;
  const hidden = body.scrollHeight - body.scrollTop - body.clientHeight;
  panel.classList.toggle('tutorial-has-more', hidden > 2);
}

/**
 * Update spotlight cutout and panel position
 */
function updateSpotlightAndPosition() {
  if (!tutorialOverlay || isMinimized) return;

  // Every branch below can change the card's height, and several return early.
  // One frame later the layout has settled whichever way it went.
  requestAnimationFrame(updateScrollCue);

  const step = activeTutorial.steps[currentStepIndex];
  const panel = tutorialOverlay.querySelector('.tutorial-panel');
  const arrow = tutorialOverlay.querySelector('.tutorial-arrow');
  const cutout = tutorialOverlay.querySelector('.tutorial-spotlight-cutout');

  // Clear previous highlight
  document.querySelectorAll('.tutorial-target-highlight').forEach((el) => {
    el.classList.remove('tutorial-target-highlight');
  });

  // Clear previous scroll locks
  document.querySelectorAll('.tutorial-scroll-locked').forEach((el) => {
    el.classList.remove('tutorial-scroll-locked');
  });

  if (
    (!step.highlightSelector && !step.targetKey) ||
    step.position === 'center'
  ) {
    // No highlight - center the panel, hide spotlight
    cutout.setAttribute('width', '0');
    cutout.setAttribute('height', '0');
    centerPanel(panel, arrow);
    currentTarget = null;
    return;
  }

  // Find the first *visible* target element using multi-step visibility check
  // Supports both targetKey (data-tutorial-target) and legacy selectors
  let target = currentTarget;
  if (!target || !document.contains(target) || !isElementVisible(target)) {
    target = resolveStepTarget(step);
    currentTarget = target;
  }

  if (!target) {
    // Target not found - center panel
    cutout.setAttribute('width', '0');
    cutout.setAttribute('height', '0');
    centerPanel(panel, arrow);
    return;
  }

  // Add highlight class to target
  target.classList.add('tutorial-target-highlight');
  adjustTutorialZIndex(target);
  // Note: We intentionally apply scroll locking *after* we ensure the target
  // is visible. Otherwise, `overflow: hidden` can prevent our own programmatic
  // scrolling and leave the highlighted control under fixed UI (mobile actions bar).

  // Get effective viewport dimensions (zoom-safe)
  const viewport = getEffectiveViewport();

  /**
   * On mobile, the bottom actions/camera bar is fixed and can cover content
   * (including elements inside the Customizer drawer). Treat that area as
   * "not visible" for spotlight interactions.
   */
  const getEffectiveViewportBottom = () => {
    // Default: use visualViewport height for zoom-safe calculations
    let bottom = viewport.height;

    // Mobile actions bar is fixed at the bottom and can overlap the Customizer drawer.
    const actionsBar = document.getElementById('actionsBar');
    if (actionsBar) {
      // If the highlighted element is *inside* the fixed actions bar (e.g. Actions/Camera
      // toggles), treat the full viewport as usable so we don't consider the target "hidden"
      // behind itself. Otherwise the spotlight can get stuck on the previous step's cutout.
      if (actionsBar.contains(target)) {
        return viewport.height;
      }

      const style = window.getComputedStyle(actionsBar);
      const rect = actionsBar.getBoundingClientRect();
      if (
        style.position === 'fixed' &&
        rect.height > 0 &&
        rect.top >= 0 &&
        rect.top < viewport.height
      ) {
        bottom = Math.min(bottom, rect.top);
      }
    }

    // Small padding so the highlighted control isn't flush against the bar.
    return Math.max(0, bottom - 8);
  };

  // Scroll target into view if needed
  const targetRect = target.getBoundingClientRect();
  const effectiveBottom = getEffectiveViewportBottom();
  // A control flush with the window's edge can measure a pixel past it (the
  // camera tab's right edge read 1281 in a 1280 window); that is on screen.
  const EDGE_TOLERANCE = 2;
  const isVisible =
    targetRect.top >= -EDGE_TOLERANCE &&
    targetRect.bottom <= effectiveBottom + EDGE_TOLERANCE &&
    targetRect.left >= -EDGE_TOLERANCE &&
    targetRect.right <= viewport.width + EDGE_TOLERANCE;

  if (!isVisible) {
    const scrollableParent = findScrollableParent(target);
    const deltaDown = targetRect.bottom - effectiveBottom;
    const topPadding = 16;

    // Two rules, and the order between them matters:
    //
    // `wanted` is where the target's top belongs. Aligning it to the window's
    // top would bury it under whatever chrome sits above its clipping box: on
    // the welcome tour's Open-or-start step #welcomeScreen's box starts 124px
    // down (148px on the CI Firefox runner), and the panel's first hundred-odd
    // pixels, halo and heading included, would be clipped away.
    //
    // `fits` decides whether showing the bottom is even sensible. On that
    // runner, wider fonts wrap the same panel to 648px inside a 572px box,
    // and a bottom-first rule would drag the top back up to y=48 under an
    // 86px header. A target too tall to fit has to show its top, because that
    // is where its heading is.
    const wanted = visibleTopBoundFor(target) + topPadding + SPOTLIGHT_PADDING;
    const fits = targetRect.height <= effectiveBottom - wanted;

    // Prefer adjusting the nearest scrollable parent so we can account for
    // fixed UI overlays (like the mobile actions bar).
    if (scrollableParent && typeof scrollableParent.scrollBy === 'function') {
      if (deltaDown > 0 && fits) {
        scrollableParent.scrollBy({
          top: deltaDown + topPadding,
          behavior: 'smooth',
        });
      } else if (targetRect.top < wanted) {
        scrollableParent.scrollBy({
          top: targetRect.top - wanted,
          behavior: 'smooth',
        });
      } else {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    } else if (typeof window.scrollBy === 'function') {
      if (deltaDown > 0 && fits) {
        window.scrollBy({ top: deltaDown + topPadding, behavior: 'smooth' });
      } else if (targetRect.top < wanted) {
        window.scrollBy({
          top: targetRect.top - wanted,
          behavior: 'smooth',
        });
      } else {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    } else {
      // Fallback for older environments
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }

    // Wait for scroll to complete before positioning
    setTimeout(() => updateSpotlightAndPosition(), 350);
    return;
  }

  // Get target bounds
  const rect = target.getBoundingClientRect();

  // Lock scrolling on parent scrollable container if requested (after scrolling)
  if (step.lockScroll) {
    const scrollableParent = findScrollableParent(target);
    if (scrollableParent) {
      scrollableParent.classList.add('tutorial-scroll-locked');
    }
  }

  // Update spotlight cutout
  cutout.setAttribute('x', rect.left - SPOTLIGHT_PADDING);
  cutout.setAttribute('y', rect.top - SPOTLIGHT_PADDING);
  cutout.setAttribute('width', rect.width + SPOTLIGHT_PADDING * 2);
  cutout.setAttribute('height', rect.height + SPOTLIGHT_PADDING * 2);

  // -------------------------------------------------------------------------
  // Mobile behavior: dock panel to top/bottom to avoid covering targets.
  // - Target near bottom → dock panel to TOP
  // - Target near top → dock panel to BOTTOM
  // If docking still blocks the target, auto-minimize (only for steps that
  // require the user to interact with the highlighted control).
  // -------------------------------------------------------------------------
  if (isMobileViewport()) {
    const safeAreas = measureSafeAreaInsets();
    const requiresUserInteraction = !!step.completion;

    const actionsBar = document.getElementById('actionsBar');
    const actionsBarRect = actionsBar?.getBoundingClientRect?.();
    const actionsBarHeight =
      actionsBarRect && window.getComputedStyle(actionsBar).position === 'fixed'
        ? Math.max(0, actionsBarRect.height || 0)
        : 0;

    // The card stays up over the open Customizer instead of collapsing to a
    // pill, so its docks have something to respect. Held upright the
    // Customizer is a sheet across the bottom of the screen: the card docks
    // above it, never on it (its title row carries Close, and the step's
    // target is often inside it). Held sideways it is a column from the top,
    // and a card starting at the viewport top would land on its Close
    // button, so a top dock starts below its title row instead.
    const drawer = isMobileDrawerOpen()
      ? document.getElementById('paramPanel')
      : null;
    const drawerRect =
      drawer && isRendered(drawer) ? drawer.getBoundingClientRect() : null;
    const sheetAcross =
      !!drawerRect && drawerRect.width >= viewport.width * 0.9;
    // offsetTop is where the sheet comes to rest; the box can still be
    // sliding in when a step arrives.
    const sheetBelow = sheetAcross
      ? Math.max(0, viewport.height - drawer.offsetTop)
      : 0;
    const drawerTitleRow =
      drawer && !sheetAcross
        ? drawer.querySelector('.panel-header-title-row')
        : null;
    const drawerHeaderBottom =
      drawerTitleRow && isRendered(drawerTitleRow)
        ? drawerTitleRow.getBoundingClientRect().bottom + 8
        : 0;

    // The top dock starts below the app's own top chrome. A card pointing at
    // something low on the screen docks upward, and at 412x810 a card
    // starting at y 8 would bury the header row and the Customizer row on its
    // way past. Docking clear of the chrome is better than disqualifying the
    // dock, which would cost the card its visibility on those steps. A bar the
    // target itself lives in is not counted: the spotlight elevates that on
    // purpose.
    const topChromeBottom = [
      '.app-header',
      '#workflowProgress',
      '.preview-drawer-header',
    ]
      .map((selector) => document.querySelector(selector))
      .filter((el) => el && isRendered(el) && !el.contains(target))
      .reduce(
        (lowest, el) => Math.max(lowest, el.getBoundingClientRect().bottom),
        0
      );

    const topOffset = Math.max(
      8,
      safeAreas.top + 8,
      drawerHeaderBottom,
      topChromeBottom + 8
    );
    const bottomOffset = Math.max(
      8,
      safeAreas.bottom + actionsBarHeight + 8,
      sheetBelow + 8
    );

    const intersects = (a, b, pad = 6) => {
      return !(
        a.right < b.left - pad ||
        a.left > b.right + pad ||
        a.bottom < b.top - pad ||
        a.top > b.bottom + pad
      );
    };

    const overlapArea = (a, b) => {
      const left = Math.max(a.left, b.left);
      const right = Math.min(a.right, b.right);
      const top = Math.max(a.top, b.top);
      const bottom = Math.min(a.bottom, b.bottom);
      const w = Math.max(0, right - left);
      const h = Math.max(0, bottom - top);
      return w * h;
    };

    const isBlockedAtCenter = () => {
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const topEl = document.elementFromPoint(cx, cy);
      return !!topEl && (panel.contains(topEl) || topEl === panel);
    };

    // A docked card must not sit on a control it is not pointing at. Both
    // tests above ask only about the step's own target, so a card that
    // cleared its target could still bury the rest of the chrome (at 412x810
    // a top-docked card could cover #uiModeToggle).
    const clearanceRects = () => {
      const out = [];
      const selector = [
        '.app-header button',
        '.app-header a',
        '#workflowProgress button',
        '.preview-drawer-header button',
        '#actionsBar button',
      ].join(', ');
      for (const el of document.querySelectorAll(selector)) {
        // The step's own target is what the spotlight elevates on purpose,
        // and the panel's own controls travel with it.
        if (panel.contains(el)) continue;
        if (el === target || el.contains(target) || target.contains(el))
          continue;
        if (!isRendered(el)) continue;
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) out.push(r);
      }
      return out;
    };

    const clearanceOverlap = (pr) =>
      clearanceRects().reduce((sum, c) => sum + overlapArea(pr, c), 0);

    // The clearance figure is wanted twice per dock — once to disqualify and
    // once to rank — and it walks the DOM, so it is measured once and passed.
    const blocks = (pr, clearance) =>
      intersects(pr, rect) || isBlockedAtCenter() || clearance > 0;

    // With the Customizer open as a column, a target inside it can sit in the
    // middle of the screen, where a full-height card covers it from either
    // side. The card then keeps to the room on its own side of the target
    // and scrolls inside, so it neither covers the target nor folds away.
    const roomBeside = (side) =>
      side === 'top'
        ? rect.top - SPOTLIGHT_PADDING - 8 - (viewport.offsetTop + topOffset)
        : viewport.height -
          bottomOffset -
          (rect.bottom + SPOTLIGHT_PADDING + 8);

    const dock = (side) => {
      // Compact floating bubble (not full-width sheet) for portrait usability.
      panel.style.position = 'fixed';
      panel.style.left = '50%';
      panel.style.right = '';
      panel.style.transform = 'translateX(-50%)';
      panel.style.width = 'min(94vw, 360px)';
      panel.style.maxWidth = '';

      if (side === 'top') {
        panel.style.top = `${viewport.offsetTop + topOffset}px`;
        panel.style.bottom = '';
      } else {
        panel.style.bottom = `${bottomOffset}px`;
        panel.style.top = '';
      }

      // Constrain height so it doesn't eat the whole screen.
      const available = Math.max(
        160,
        viewport.height - topOffset - bottomOffset - 16
      );
      let cap = Math.min(available, viewport.height * 0.45);
      if (drawerRect && !sheetAcross) {
        const room = roomBeside(side);
        if (room >= 160) cap = Math.min(cap, room);
      }
      panel.style.maxHeight = `${cap}px`;

      panel.classList.add('tutorial-panel-positioned');
      panel.classList.remove('tutorial-panel-centered');
      arrow.style.display = 'none';
    };

    const targetCenterY = rect.top + rect.height / 2;
    const preferTop = targetCenterY > viewport.height * 0.6;
    const first = preferTop ? 'top' : 'bottom';
    const second = preferTop ? 'bottom' : 'top';

    dock(first);
    const pr1 = panel.getBoundingClientRect();
    const c1 = clearanceOverlap(pr1);
    if (blocks(pr1, c1)) {
      dock(second);
      const pr2 = panel.getBoundingClientRect();
      const c2 = clearanceOverlap(pr2);
      if (blocks(pr2, c2)) {
        // If the highlighted target is a large region (e.g. preview container),
        // it may be impossible to avoid overlap. Only auto-minimize when the
        // current step is gating progress on a required interaction.
        if (requiresUserInteraction) {
          // Last resort: auto-minimize so the user can tap the target.
          setMinimized(true, { auto: true });
          return;
        }

        // Non-interactive step: keep the panel visible, choosing the docked
        // side that buries the least — the target it points at plus
        // the chrome it does not.
        const a1 = overlapArea(pr1, rect) + c1;
        const a2 = overlapArea(pr2, rect) + c2;
        if (a1 < a2) {
          dock(first);
        }
        restoreIfAutoMinimized();
        return;
      }
    }

    // If we were auto-minimized on the previous step but this step fits, restore.
    restoreIfAutoMinimized();
    return;
  }

  // Measure actual panel dimensions for accurate positioning
  applyZoomAdjustments(panel);

  // Clear any mobile docking overrides
  panel.style.width = '';
  panel.style.maxHeight = '';
  const panelRect = panel.getBoundingClientRect();

  // Calculate best position for panel using measured dimensions
  const position = calculateBestPosition(rect, step.position, panelRect);
  positionPanel(panel, arrow, rect, position);
  panel.classList.add('tutorial-panel-positioned');
  panel.classList.remove('tutorial-panel-centered');

  // Desktop/tablet: keep arrow enabled as-is (no further changes).
}

/**
 * Get effective viewport dimensions accounting for visualViewport API
 * Handles mobile zoom, on-screen keyboard, and browser chrome changes
 * @returns {{width: number, height: number, offsetTop: number, offsetLeft: number, scale: number}}
 */
function getEffectiveViewport() {
  // Use visualViewport API if available (handles zoom, keyboard, browser chrome)
  if (window.visualViewport) {
    return {
      width: window.visualViewport.width,
      height: window.visualViewport.height,
      offsetTop: window.visualViewport.offsetTop,
      offsetLeft: window.visualViewport.offsetLeft,
      scale: window.visualViewport.scale,
    };
  }

  // Fallback to window dimensions
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    offsetTop: 0,
    offsetLeft: 0,
    scale: 1,
  };
}

/**
 * Adjust panel sizing for high zoom levels
 * @param {HTMLElement} panel - Tutorial panel element
 */
function applyZoomAdjustments(panel) {
  if (!panel) return;
  const zoom = window.visualViewport?.scale || 1;
  if (zoom > 1.5) {
    panel.style.maxWidth = '280px';
  } else {
    panel.style.maxWidth = '';
  }
}

/**
 * Measure safe area insets for notched devices via CSS env() variables
 * @returns {{top: number, right: number, bottom: number, left: number}}
 */
function measureSafeAreaInsets() {
  // Measure each inset separately to avoid CSS conflicts
  const measure = (prop, anchor) => {
    const testEl = document.createElement('div');
    testEl.style.cssText = `
      position: fixed;
      ${anchor}: env(safe-area-inset-${prop}, 0px);
      pointer-events: none;
      visibility: hidden;
      width: 1px;
      height: 1px;
    `;
    document.body.appendChild(testEl);
    const rect = testEl.getBoundingClientRect();
    document.body.removeChild(testEl);

    if (prop === 'top') return rect.top;
    if (prop === 'left') return rect.left;
    if (prop === 'right') return window.innerWidth - rect.right;
    if (prop === 'bottom') return window.innerHeight - rect.bottom;
    return 0;
  };

  return {
    top: measure('top', 'top'),
    right: measure('right', 'right'),
    bottom: measure('bottom', 'bottom'),
    left: measure('left', 'left'),
  };
}

/**
 * Calculate best position for panel relative to target
 * Uses measured panel dimensions instead of fixed constants
 * @param {DOMRect} targetRect - Target element bounds
 * @param {string} preferred - Preferred position
 * @param {DOMRect} panelRect - Measured panel dimensions
 * @returns {string} - Position: 'top', 'bottom', 'left', 'right'
 */
function calculateBestPosition(targetRect, preferred, panelRect) {
  const panelWidth = panelRect?.width || 340;
  const panelHeight = panelRect?.height || 250;

  // Use effective viewport (handles zoom, keyboard, browser chrome)
  const viewport = getEffectiveViewport();
  const safeAreas = measureSafeAreaInsets();

  // Calculate available space accounting for safe areas
  const viewportTop = viewport.offsetTop;
  const viewportBottom = viewport.offsetTop + viewport.height;
  const viewportLeft = viewport.offsetLeft;
  const viewportRight = viewport.offsetLeft + viewport.width;

  const space = {
    top: targetRect.top - (viewportTop + safeAreas.top),
    bottom: viewportBottom - targetRect.bottom - safeAreas.bottom,
    left: targetRect.left - (viewportLeft + safeAreas.left),
    right: viewportRight - targetRect.right - safeAreas.right,
  };

  // If preferred position has enough space, use it
  if (preferred && preferred !== 'auto') {
    const minSpace =
      preferred === 'top' || preferred === 'bottom'
        ? panelHeight + PANEL_OFFSET
        : panelWidth + PANEL_OFFSET;
    if (space[preferred] >= minSpace) {
      return preferred;
    }
  }

  // Find best position with most space
  const positions = [
    { pos: 'bottom', space: space.bottom, needed: panelHeight + PANEL_OFFSET },
    { pos: 'top', space: space.top, needed: panelHeight + PANEL_OFFSET },
    { pos: 'right', space: space.right, needed: panelWidth + PANEL_OFFSET },
    { pos: 'left', space: space.left, needed: panelWidth + PANEL_OFFSET },
  ];

  // Prefer positions with enough space, sorted by available space
  const viable = positions.filter((p) => p.space >= p.needed);
  if (viable.length > 0) {
    return viable.sort((a, b) => b.space - a.space)[0].pos;
  }

  // Fallback: use position with most space
  return positions.sort((a, b) => b.space - a.space)[0].pos;
}

/**
 * Position the panel and arrow relative to target
 * Uses visualViewport and safe area insets for reliable positioning
 * @param {HTMLElement} panel - Tutorial panel
 * @param {HTMLElement} arrow - Arrow element
 * @param {DOMRect} targetRect - Target bounds
 * @param {string} position - Position
 */
function positionPanel(panel, arrow, targetRect, position) {
  const panelRect = panel.getBoundingClientRect();
  const viewport = getEffectiveViewport();
  const safeAreas = measureSafeAreaInsets();

  // Calculate margins including safe areas
  const marginTop = Math.max(16, safeAreas.top);
  const marginBottom = Math.max(16, safeAreas.bottom);
  const marginLeft = Math.max(16, safeAreas.left);
  const marginRight = Math.max(16, safeAreas.right);
  const viewportTop = viewport.offsetTop;
  const viewportBottom = viewport.offsetTop + viewport.height;
  const viewportLeft = viewport.offsetLeft;
  const viewportRight = viewport.offsetLeft + viewport.width;

  let top, left;
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  // Reset transforms
  panel.style.transform = '';

  // Arrow position constraints - keep within panel bounds
  const minArrowOffset = 20;
  const maxArrowOffsetX = Math.max(minArrowOffset, panelRect.width - 40);
  const maxArrowOffsetY = Math.max(minArrowOffset, panelRect.height - 40);

  switch (position) {
    case 'top':
      top =
        targetRect.top - panelRect.height - PANEL_OFFSET - SPOTLIGHT_PADDING;
      left = Math.max(
        viewportLeft + marginLeft,
        Math.min(
          targetCenterX - panelRect.width / 2,
          viewportRight - panelRect.width - marginRight
        )
      );
      arrow.className = 'tutorial-arrow tutorial-arrow-bottom';
      arrow.style.left = `${Math.min(Math.max(targetCenterX - left - 8, minArrowOffset), maxArrowOffsetX)}px`;
      arrow.style.top = '';
      break;

    case 'bottom':
      top = targetRect.bottom + PANEL_OFFSET + SPOTLIGHT_PADDING;
      left = Math.max(
        viewportLeft + marginLeft,
        Math.min(
          targetCenterX - panelRect.width / 2,
          viewportRight - panelRect.width - marginRight
        )
      );
      arrow.className = 'tutorial-arrow tutorial-arrow-top';
      arrow.style.left = `${Math.min(Math.max(targetCenterX - left - 8, minArrowOffset), maxArrowOffsetX)}px`;
      arrow.style.top = '';
      break;

    case 'left':
      left =
        targetRect.left - panelRect.width - PANEL_OFFSET - SPOTLIGHT_PADDING;
      top = Math.max(
        viewportTop + marginTop,
        Math.min(
          targetCenterY - panelRect.height / 2,
          viewportBottom - panelRect.height - marginBottom
        )
      );
      arrow.className = 'tutorial-arrow tutorial-arrow-right';
      arrow.style.top = `${Math.min(Math.max(targetCenterY - top - 8, minArrowOffset), maxArrowOffsetY)}px`;
      arrow.style.left = '';
      break;

    case 'right':
      left = targetRect.right + PANEL_OFFSET + SPOTLIGHT_PADDING;
      top = Math.max(
        viewportTop + marginTop,
        Math.min(
          targetCenterY - panelRect.height / 2,
          viewportBottom - panelRect.height - marginBottom
        )
      );
      arrow.className = 'tutorial-arrow tutorial-arrow-left';
      arrow.style.top = `${Math.min(Math.max(targetCenterY - top - 8, minArrowOffset), maxArrowOffsetY)}px`;
      arrow.style.left = '';
      break;
  }

  // Clamp to effective viewport with safe area margins
  top = Math.max(
    viewportTop + marginTop,
    Math.min(top, viewportBottom - panelRect.height - marginBottom)
  );
  left = Math.max(
    viewportLeft + marginLeft,
    Math.min(left, viewportRight - panelRect.width - marginRight)
  );

  panel.style.position = 'fixed';
  panel.style.top = `${top}px`;
  panel.style.left = `${left}px`;
  arrow.style.display = 'block';
}

/**
 * Adjust tutorial z-index when targeting elements with high stacking contexts
 * @param {HTMLElement} targetElement - Target element
 */
function adjustTutorialZIndex(targetElement) {
  if (!tutorialOverlay || !targetElement) return;

  const rootStyles = getComputedStyle(document.documentElement);
  const baseBackdrop =
    parseInt(rootStyles.getPropertyValue('--z-index-tutorial-backdrop'), 10) ||
    10005;

  let maxZ = 0;
  const collectZ = (el) => {
    // Skip the target's own highlight. The caller adds
    // .tutorial-target-highlight one line before calling this, and that class
    // elevates the target to --z-index-tutorial-highlight; reading it back
    // would make the overlay measure its own work and climb above its own
    // head on every highlighted step, burying dialogs at --z-index-modal. The
    // overlay starts above the whole highlight family by token, so this
    // function only has to handle genuinely high-z ancestors in the page.
    if (el.classList.contains('tutorial-target-highlight')) return;
    const zIndex = parseInt(getComputedStyle(el).zIndex, 10);
    if (!Number.isNaN(zIndex)) {
      maxZ = Math.max(maxZ, zIndex);
    }
  };

  collectZ(targetElement);
  let parent = targetElement.parentElement;
  while (parent && parent !== document.body) {
    collectZ(parent);
    parent = parent.parentElement;
  }

  if (maxZ >= baseBackdrop) {
    tutorialOverlay.style.setProperty(
      '--z-index-tutorial-backdrop',
      String(maxZ + 1)
    );
    tutorialOverlay.style.setProperty(
      '--z-index-tutorial-spotlight',
      String(maxZ + 2)
    );
    tutorialOverlay.style.setProperty(
      '--z-index-tutorial-panel',
      String(maxZ + 3)
    );
    tutorialOverlay.style.setProperty(
      '--z-index-tutorial-minimized',
      String(maxZ + 3)
    );
  } else {
    tutorialOverlay.style.removeProperty('--z-index-tutorial-backdrop');
    tutorialOverlay.style.removeProperty('--z-index-tutorial-spotlight');
    tutorialOverlay.style.removeProperty('--z-index-tutorial-panel');
    tutorialOverlay.style.removeProperty('--z-index-tutorial-minimized');
  }
}

/**
 * Setup step completion gating
 * @param {TutorialStep} step
 */
function setupCompletion(step) {
  const requirementEl = tutorialOverlay?.querySelector('#tutorialRequirement');
  const nextBtn = tutorialOverlay?.querySelector('#tutorialNextBtn');

  if (!requirementEl || !nextBtn) return;

  stepCompleted = !step.completion;

  if (step.completion) {
    requirementEl.textContent = REQUIREMENT_PENDING_TEXT;
    requirementEl.classList.remove('tutorial-requirement-done');
  } else {
    requirementEl.textContent = '';
  }

  if (stepCompleted) nextBtn.disabled = false;
  else {
    const backBtn = tutorialOverlay?.querySelector('#tutorialBackBtn');
    disableKeepingFocus(nextBtn, backBtn?.disabled ? null : backBtn);
  }
  nextBtn.setAttribute('aria-disabled', String(!stepCompleted));

  if (!step.completion) return;

  const { type, selector, event, trigger } = step.completion;

  if (type === 'detailsOpen') {
    attachDetailsOpenListener(selector);
  } else if (type === 'domEvent') {
    attachCompletionListener({ selector, event, predicate: null });
  } else if (type === 'modalOpen') {
    // Watch for modal to become visible (lose 'hidden' class)
    attachModalOpenListener(selector, trigger);
  } else if (type === 'modalClose') {
    // Watch for modal to become hidden (gain 'hidden' class)
    attachModalCloseListener(selector);
  }
}

/**
 * Attach a completion listener
 */
function attachCompletionListener({ selector, event, predicate }) {
  const requirementEl = tutorialOverlay?.querySelector('#tutorialRequirement');
  const nextBtn = tutorialOverlay?.querySelector('#tutorialNextBtn');
  if (!requirementEl || !nextBtn) return;

  // Support selector lists like "#a, #b" and tolerate missing nodes. Every
  // element the list names is watched: a hint can offer more than one way
  // (drag the slider or type in the number box) and any of them completes it.
  const targets = Array.from(document.querySelectorAll(selector));
  if (targets.length === 0) {
    // Wait for element to appear
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        observer.disconnect();
        attachCompletionListener({ selector, event, predicate });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    completionListeners.push({ observer });
    return;
  }

  let done = false;
  const handler = (e) => {
    if (done) return;
    if (predicate && !predicate(e.currentTarget)) return;
    done = true;

    stepCompleted = true;
    nextBtn.disabled = false;
    nextBtn.setAttribute('aria-disabled', 'false');
    requirementEl.textContent = '✓ Done! Click Next to continue';
    requirementEl.classList.add('tutorial-requirement-done');
    announceToScreenReader('Action completed. Next enabled.');
    // If we auto-minimized to let the user interact, bring the panel back.
    setTimeout(() => restoreIfAutoMinimized(), 250);

    for (const target of targets) {
      target.removeEventListener(event, handler, true);
    }
    completionListeners = completionListeners.filter(
      (l) => l.handler !== handler
    );
  };

  for (const target of targets) {
    target.addEventListener(event, handler, true);
    completionListeners.push({ element: target, event, handler });
  }
}

/**
 * Attach a completion listener for a <details> element, even when the selector
 * points at a wrapper element containing the <details>.
 */
function attachDetailsOpenListener(selector) {
  const requirementEl = tutorialOverlay?.querySelector('#tutorialRequirement');
  const nextBtn = tutorialOverlay?.querySelector('#tutorialNextBtn');
  if (!requirementEl || !nextBtn) return;

  const resolveDetails = () => {
    // Selector may be a list; pick the first element we can resolve to a <details>.
    const el = document.querySelector(selector);
    if (!el) return null;
    if (el instanceof HTMLDetailsElement) return el;
    return el.querySelector?.('details') instanceof HTMLDetailsElement
      ? el.querySelector('details')
      : null;
  };

  const details = resolveDetails();
  if (!details) {
    const observer = new MutationObserver(() => {
      const d = resolveDetails();
      if (d) {
        observer.disconnect();
        attachDetailsOpenListener(selector);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    completionListeners.push({ observer });
    return;
  }

  const handler = () => {
    if (!details.open) return;
    stepCompleted = true;
    nextBtn.disabled = false;
    nextBtn.setAttribute('aria-disabled', 'false');
    requirementEl.textContent = '✓ Done! Click Next to continue';
    requirementEl.classList.add('tutorial-requirement-done');
    announceToScreenReader('Action completed. Next enabled.');
    setTimeout(() => restoreIfAutoMinimized(), 250);

    details.removeEventListener('toggle', handler, true);
    completionListeners = completionListeners.filter(
      (l) => l.handler !== handler
    );
  };

  details.addEventListener('toggle', handler, true);
  completionListeners.push({ element: details, event: 'toggle', handler });

  // If it's already open (or was toggled before we attached), mark complete immediately.
  if (details.open) {
    handler();
  }
}

/**
 * Attach a modal open listener using MutationObserver
 */
function attachModalOpenListener(modalSelector, triggerSelector) {
  const requirementEl = tutorialOverlay?.querySelector('#tutorialRequirement');
  const nextBtn = tutorialOverlay?.querySelector('#tutorialNextBtn');
  if (!requirementEl || !nextBtn) return;

  const modal = document.querySelector(modalSelector);
  if (!modal) {
    console.warn(`Modal not found: ${modalSelector}`);
    return;
  }

  // Also set up click listener on trigger button to ensure it opens
  const triggerBtn = document.querySelector(triggerSelector);
  if (triggerBtn) {
    const clickHandler = () => {
      // Give modal time to open
      setTimeout(() => {
        if (!modal.classList.contains('hidden')) {
          markStepComplete();
        }
      }, 100);
    };
    triggerBtn.addEventListener('click', clickHandler, true);
    completionListeners.push({
      element: triggerBtn,
      event: 'click',
      handler: clickHandler,
    });
  }

  // Watch for class changes on modal
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'class') {
        if (!modal.classList.contains('hidden')) {
          markStepComplete();
          observer.disconnect();
        }
      }
    });
  });

  observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
  completionListeners.push({ observer });

  function markStepComplete() {
    stepCompleted = true;
    nextBtn.disabled = false;
    nextBtn.setAttribute('aria-disabled', 'false');
    requirementEl.textContent = '✓ Done! Click Next to continue';
    requirementEl.classList.add('tutorial-requirement-done');
    announceToScreenReader('Help opened. Next enabled.');

    // Auto-advance to show the modal
    setTimeout(() => {
      if (stepCompleted && currentStepIndex < activeTutorial.steps.length - 1) {
        showStep(currentStepIndex + 1);
      }
    }, 800);
  }
}

/**
 * Attach a modal close listener using MutationObserver
 */
function attachModalCloseListener(modalSelector) {
  const requirementEl = tutorialOverlay?.querySelector('#tutorialRequirement');
  const nextBtn = tutorialOverlay?.querySelector('#tutorialNextBtn');
  if (!requirementEl || !nextBtn) return;

  const modal = document.querySelector(modalSelector);
  if (!modal) {
    console.warn(`Modal not found: ${modalSelector}`);
    return;
  }

  // Watch for class changes on modal
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'class') {
        if (modal.classList.contains('hidden')) {
          stepCompleted = true;
          nextBtn.disabled = false;
          nextBtn.setAttribute('aria-disabled', 'false');
          requirementEl.textContent = '✓ Done! Click Next to continue';
          requirementEl.classList.add('tutorial-requirement-done');
          announceToScreenReader('Help closed. Next enabled.');
          observer.disconnect();
        }
      }
    });
  });

  observer.observe(modal, { attributes: true, attributeFilter: ['class'] });
  completionListeners.push({ observer });
}

/**
 * Clear all completion listeners
 */
function clearCompletionListeners() {
  completionListeners.forEach(({ element, event, handler, observer }) => {
    if (observer) {
      observer.disconnect();
    } else if (element && handler) {
      element.removeEventListener(event, handler, true);
    }
  });
  completionListeners = [];
  stepCompleted = true;
}

/**
 * Close a tour when the app surface it belongs to goes away. A welcome
 * tour ends when a project opens through a spotlight cutout; a project tour
 * ends when the Main Page comes back (its last step points at the button
 * that does exactly that).
 *
 * Mirrors handleModeChangeDuringTutorial: the user's action wins, progress
 * is saved past step one, and the close announces its own reason.
 *
 * @param {'welcome'|'project'} homeSurface The surface this tour is built for
 */
function watchSurfaceChangeDuringTutorial(homeSurface) {
  surfaceObserver = new MutationObserver(() => {
    if (!activeTutorial) return;
    if (document.body.dataset.appSurface === homeSurface) return;

    const title = activeTutorial.title;
    const progressSaved = currentStepIndex > 0;
    if (progressSaved) {
      saveTutorialProgress(currentStepIndex);
    }

    closeTutorial(false, { skipModeRestore: true, skipAnnouncement: true });

    const reason =
      homeSurface === 'welcome'
        ? 'a project opened'
        : 'you went back to the Main Page';
    announceToScreenReader(
      `${title} closed because ${reason}.${progressSaved ? ' Progress saved.' : ''}`,
      'assertive'
    );
  });
  surfaceObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['data-app-surface'],
  });
}

/**
 * A user interface switch out of the tour's own interfaces wins over a
 * running tutorial: close cleanly with progress saved and one
 * announcement, never switch the user back, and hand back any Classic
 * density the tutorial's own forced switch planted. A switch within them
 * keeps the tour (see below).
 * @param {string} newMode - Mode the user switched into
 */
function handleModeChangeDuringTutorial(newMode) {
  if (!activeTutorial) return;

  // A switch inside the interface family this tour was built for is the
  // user exploring, not the user leaving, so the tour stays and the step
  // re-settles. Crossing between Forge and Classic still ends it, because
  // the steps point at chrome the other layout does not have.
  const homeModes = activeTutorial.homeModes || FORGE_HOME_MODES;
  if (homeModes.includes(newMode)) {
    // The user's own choice outlives the tour: closeTutorial must not switch
    // them back to where the tour found them.
    tutorialForcedMode = false;
    preTutorialMode = null;
    preTutorialDensity = null;
    void reResolveStepAfterLayoutChange('the interface changed');
    return;
  }

  const title = activeTutorial.title;
  const stepsTotal = activeTutorial.steps?.length || 0;
  const stepCurrent = currentStepIndex + 1;

  const progressSaved = currentStepIndex > 0;
  if (progressSaved) {
    saveTutorialProgress(currentStepIndex);
  }

  const densityToRepair =
    tutorialForcedMode && newMode === 'classic' ? preTutorialDensity : null;

  closeTutorial(false, { skipModeRestore: true, skipAnnouncement: true });

  if (densityToRepair) {
    getUIModeController().setClassicDensity(densityToRepair, {
      skipAnnouncement: true,
    });
  }

  announceToScreenReader(
    `${title} tutorial closed at step ${stepCurrent} of ${stepsTotal} because the interface changed.${progressSaved ? ' Progress saved.' : ''}`,
    'assertive'
  );
}

/**
 * Close the tutorial and clean up
 * @param {boolean} completed - Whether tutorial was completed (vs canceled)
 * @param {Object} [options]
 * @param {boolean} [options.skipModeRestore] - Do not switch back to the
 *   pre-tutorial mode (the close was caused by the user's own mode switch)
 * @param {boolean} [options.skipAnnouncement] - Caller announces instead
 */
export function closeTutorial(completed = false, options = {}) {
  if (!tutorialOverlay) return;

  if (modeChangeUnsubscribe) {
    modeChangeUnsubscribe();
    modeChangeUnsubscribe = null;
  }

  if (densityChangeListener) {
    document.removeEventListener(
      'classic-density-change',
      densityChangeListener
    );
    densityChangeListener = null;
  }

  clearCompletionListeners();
  clearDrawerObserver();

  // Clean up focus trap
  if (focusTrapCleanup) {
    focusTrapCleanup();
    focusTrapCleanup = null;
  }

  // Clean up target removal observer
  if (targetRemovalObserver) {
    targetRemovalObserver.disconnect();
    targetRemovalObserver = null;
  }

  // Clean up the welcome-surface flip observer
  if (surfaceObserver) {
    surfaceObserver.disconnect();
    surfaceObserver = null;
  }

  // Clean up the dialog watcher
  if (dialogObserver) {
    dialogObserver.disconnect();
    dialogObserver = null;
  }

  // Drop what the stand-down was holding without restoring it - the sweep
  // below strips every highlight anyway, and the overlay is about to go.
  dialogStandDown = null;

  // Remove highlight from any targeted elements
  document.querySelectorAll('.tutorial-target-highlight').forEach((el) => {
    el.classList.remove('tutorial-target-highlight');
  });

  // Remove scroll locks
  document.querySelectorAll('.tutorial-scroll-locked').forEach((el) => {
    el.classList.remove('tutorial-scroll-locked');
  });

  // Unlock body scroll
  unlockBodyScroll();

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  // Clear debounce timeout
  if (repositionTimeout) {
    cancelAnimationFrame(repositionTimeout);
    repositionTimeout = null;
  }

  // Remove all event listeners
  window.removeEventListener('scroll', scheduleReposition);
  window.removeEventListener('orientationchange', handleOrientationChange);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  window.removeEventListener('popstate', handlePopState);
  document.removeEventListener('visibilitychange', handleVisibilityChange);

  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', scheduleReposition);
    window.visualViewport.removeEventListener('scroll', scheduleReposition);
  }

  if (screen.orientation) {
    screen.orientation.removeEventListener('change', handleOrientationChange);
  }

  document.removeEventListener('keydown', noteDialogBeforeKeydown, true);
  document.removeEventListener('keydown', handleKeydown);
  escapeOwnedElsewhere = false;

  tutorialOverlay.remove();
  tutorialOverlay = null;
  announceTutorialRunState(false);

  // Store step info for announcement before clearing
  const stepsTotal = activeTutorial?.steps?.length || 0;
  const currentStep = currentStepIndex + 1;

  // Clear or keep progress based on completion. The persistent registry
  // record is written first: clearTutorialProgress erases the only other
  // evidence this run happened.
  if (completed) {
    if (activeTutorial?.id) {
      recordTutorialCompleted(activeTutorial.id);
    }
    clearTutorialProgress();
  }

  // Focus goes back to the control that opened the tour, else to whatever
  // had focus before, and never silently to <body>, which would drop a
  // keyboard or screen-reader user at the top of the document.
  //
  // - previousFocus and triggerElement are module-level and are cleared as
  //   soon as the callback below is scheduled, so they are captured into
  //   locals here.
  // - An element's own display/visibility does not report a hidden
  //   ancestor, so canTakeFocus asks isRendered(), which uses
  //   checkVisibility().
  // - The trigger is tried first. Where a browser leaves focus after a
  //   click differs (on WebKit, clicking "Take the tour" leaves
  //   `previousFocus` reading #main-content), and somebody pressed that
  //   control to open the tour, so that control is where leaving the tour
  //   should put them.
  const restoreTo = triggerElement;
  const restoreFallback = previousFocus;

  const canTakeFocus = (el) =>
    el &&
    typeof el.focus === 'function' &&
    document.contains(el) &&
    isRendered(el);

  // Restore focus to the control that opened the tutorial.
  // Use requestAnimationFrame to ensure DOM has settled after overlay removal
  requestAnimationFrame(() => {
    let focusRestored = false;

    // First try: the control that opened the tutorial
    if (canTakeFocus(restoreTo)) {
      restoreTo.focus();
      focusRestored = document.activeElement === restoreTo;
    }

    // Second try: whatever had focus before it opened
    if (!focusRestored && canTakeFocus(restoreFallback)) {
      restoreFallback.focus();
      focusRestored = document.activeElement === restoreFallback;
    }

    // Fallback: focus a rendered, focusable element
    if (!focusRestored) {
      const fallbackTargets = [
        '#primaryActionBtn:not([disabled])',
        '#parametersContainer button:not([disabled])',
        '#featuresGuideBtn',
        '#clearFileBtn',
        '.param-control input:not([disabled])',
        '.param-control select:not([disabled])',
        '.btn-primary:not([disabled])',
        '#main-content',
      ];

      for (const selector of fallbackTargets) {
        const el = document.querySelector(selector);
        if (canTakeFocus(el)) {
          el.focus();
          if (document.activeElement === el) {
            focusRestored = true;
            break;
          }
        }
      }
    }

    // Last resort: focus body to at least have something focused
    if (!focusRestored) {
      document.body.focus();
    }
  });

  previousFocus = null;
  triggerElement = null;

  // Restore pre-tutorial UI mode if the tutorial changed it - unless the
  // close was caused by the user's own switch, which must stand.
  if (preTutorialMode && !options.skipModeRestore) {
    const modeCtrl = getUIModeController();
    if (modeCtrl.getMode() !== preTutorialMode) {
      const restored = modeCtrl.switchMode(preTutorialMode, {
        skipAnnouncement: true,
        skipFocus: true,
      });
      if (
        restored &&
        tutorialForcedMode &&
        preTutorialMode === 'classic' &&
        preTutorialDensity
      ) {
        // The restore switch itself records the tutorial's forced mode as
        // the Classic density; hand the user's real density back.
        modeCtrl.setClassicDensity(preTutorialDensity, {
          skipAnnouncement: true,
        });
      }
      if (!restored && preTutorialMode === 'classic') {
        // The viewport gate refused Classic (window too small). Never
        // strand the user silently in a mode they did not choose.
        announceToScreenReader(
          'Classic needs a wider window right now, so you are staying in Assistive Forge. Your file stays open.'
        );
      }
    }
  }
  preTutorialMode = null;
  preTutorialDensity = null;
  tutorialForcedMode = false;

  activeTutorial = null;
  currentStepIndex = 0;
  isMinimized = false;
  currentTarget = null;
  consecutiveFailures = 0;

  // A finished tour has already said so; "closed at step 13 of 13" after it
  // sounded like the tour had been given up.
  if (!options.skipAnnouncement && !completed) {
    announceToScreenReader(
      `Tutorial closed at step ${currentStep} of ${stepsTotal}.`
    );
  }
}

/**
 * Announce message to screen readers with optional politeness control.
 * Delegates to shared announcer.js utility using dual live regions
 * (avoids mutating aria-live attribute which can cause issues).
 * @param {string} message - Message to announce
 * @param {string} politeness - 'polite' (default) or 'assertive'
 */
function announceToScreenReader(message, politeness = 'polite') {
  if (politeness === 'assertive') {
    announceError(message, { clearDelayMs: 1000 });
  } else {
    announceImmediate(message, { clearDelayMs: 1000 });
  }
}
